// components/BuildingDashboard.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useRegisterTopEscapeClose } from '../hooks/useEscapeCloseStack';
import {
  DollarSign,
  TrendingUp,
  BarChart,
  Building,
  FileStack,
  FileCheck,
  ClipboardList,
  Info,
  FileUp,
  ChevronDown,
  ChevronRight,
  X,
  LayoutList,
  MessageCircle,
  UserRoundX,
  UserRoundCheck,
  UsersRound,
  Megaphone,
  Loader2,
  Lock,
} from 'lucide-react';
import DirectChatModal from './directChat/DirectChatModal';
import {
  buildShareRecipientConversationSpec,
  buildBrokerClientConversationSpec,
  findShareTokenForDirectChat,
} from '../services/directChatService';
import { subscribeAggregatedShareParticipants } from '../services/shareParticipantsService';
import { broadcastOwnerMessageToShareRecipients } from '../services/ownerBroadcastService';
import ScenarioList from './ScenarioList';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeFinancementDossiersForProperty,
  subscribeFinancementDossiersByPropertyAndBroker,
  cancelFinancementDossier,
} from '../services/financementDossierService';
import {
  getInterestForRecipient,
  createInterest,
  submitNda,
  confirmInterest,
  submitCessionContract,
  subscribeInterests,
  approveNda,
  grantDocumentAccess,
  revokeDocumentAccess,
  refuseDocumentAccess,
} from '../services/shareInterestService';
import { formatNorthAmericanPhoneInput, phoneDigitsOnly } from '../utils/phoneFormat';
import { setUserProfile } from '../services/userProfileService';
import {
  subscribeSharesByProperty,
  finalizeCession,
  subscribeShareRevokedRecipients,
  revokeDealShareRecipientAccess,
  restoreDealShareRecipientAccess,
} from '../services/shareService';
import PocketDealListingHero from './shared/PocketDealListingHero';
import MandatedInterestOffersPanel from './share/MandatedInterestOffersPanel';
import RequestDossierModal from './financementDossier/RequestDossierModal';
import RentRegisterModal from './rentRegister/RentRegisterModal';
import CreateOfferModal from './privateLenderOffer/CreateOfferModal';
import {
  subscribePrivateLenderOffersByPropertyAndLender,
  subscribePrivateLenderOffersForProperty,
  rejectPrivateLenderOffer,
  acceptPrivateLenderOfferWithSignedContract,
  cancelPrivateLenderOfferByLender,
  getOfferFileDownloadUrl,
  getPrivateLenderOffer,
  summarizeOfferContract,
} from '../services/privateLenderOfferService';
import { createFinancedDeal, getFinancedDealByOfferId, updateFinancedDeal } from '../services/financedDealsService';
import { createFinancedDealTranche, getFinancedDealTranchesByDeal } from '../services/financedDealTranchesService';
import { getLenderSettings } from '../services/lenderSettingsService';
import {
  getCurrentPeriod,
  getInterestPayment,
  getInterestPaymentsForDeal,
  getSchedulePeriodsForDeal,
  computeExpectedInterestForPeriod,
  getExpectedPaymentDateForPeriod,
  isDealActiveInPeriod,
  normalizeInterestPeriodAnchorDay,
} from '../services/interestPaymentsService';
import { updatePrivateLenderOffer } from '../services/privateLenderOfferService';
import {
  revokeBrokerPropertyDashboardAccess,
  restoreBrokerPropertyAccess,
  subscribeBrokerAccessRevoked,
  getPropertyBrokerAccessUids,
  getPropertyBrokerClientId,
  propertyHasActiveBrokerDashboardShare,
} from '../services/dataService';
import { getBrokerPublicProfile } from '../services/brokerDisplayNameService';
import { getClient } from '../services/clientsService';
import { serverTimestamp } from 'firebase/firestore';
import { isOwnedDeal } from '../utils/dealPurpose';
import calculateRentability from '../utils/calculateRentability';

function recipientProfessionalBadgeKey(persona) {
  if (persona === 'preteur_prive') return 'building.recipientBadgePrivateLender';
  if (persona === 'courtier_hypo') return 'building.recipientBadgeMortgageBroker';
  if (persona === 'courtier_immo') return 'building.recipientBadgeRealEstateBroker';
  return 'building.recipientBadgeProfessional';
}

/** Badge « partage par lien » + type pro quand le persona est connu (brokerDisplayNames). */
function recipientLinkShareBadgeKey(persona) {
  if (persona === 'courtier_hypo') return 'building.recipientBadgeLinkShareCourtierHypo';
  if (persona === 'courtier_immo') return 'building.recipientBadgeLinkShareCourtierImmo';
  if (persona === 'preteur_prive') return 'building.recipientBadgeLinkSharePrivateLender';
  return 'building.recipientBadgeLinkShare';
}

const BuildingDashboard = ({
  property,
  onCreateScenario,
  onEditScenario,
  onViewScenario,
  onEditProperty,
  onBack,
  onOpenProjectDocuments = null,
  onSaveRentRegister = null,
  onOpenFinancementDossier = null,
  clients = [],
  isCourtierHypo = false,
  isPreteurPrive = false,
  readOnly = false,
  staticScenarios = null,
  shareToken = null,
  shareData = null,
  baseScenarios = null,
  shareCreatorInfo = null,
  shareFilterByCreatorUid = null,
  additionalScenariosFromShares = null,
  financedDealsAsLender = [],
  financedDealsAsOwner = [],
  onOpenLoanDetail = null,
  onOpenDealFromChat = null,
  /** Partagé avec moi : texte lecture / lecture-écriture, au-dessus du titre du tableau de bord */
  shareAccessBannerText = null,
  /** Deal partagé depuis l’accueil : retirer l’entrée « Partagés avec moi » */
  onRemoveFromSharedDashboard = null,
  /** Partage pocket : fiche immeuble (photo, description, métriques) au lieu du simple grillage KPI */
  pocketListingLayout = false,
}) => {
  const { t, language } = useLanguage();
  const { currentUser, refreshFinancedDeals, userProfile, refreshUserProfile } = useAuth();
  const dealsForThisProperty = (financedDealsAsLender || []).filter((d) => d.propertyId === property?.id);
  const dealsForThisPropertyAsOwner = (financedDealsAsOwner || []).filter((d) => d.propertyId === property?.id && d.ownerUid === currentUser?.uid);
  const [financementDossiers, setFinancementDossiers] = useState([]);
  const [brokerDossiersForThisProperty, setBrokerDossiersForThisProperty] = useState([]);
  const [requestDossierModalOpen, setRequestDossierModalOpen] = useState(false);
  const [rentRegisterModalOpen, setRentRegisterModalOpen] = useState(false);
  const [cancelDossierConfirm, setCancelDossierConfirm] = useState(null);
  const [shareInterest, setShareInterest] = useState(null);
  const [shareInterestLoading, setShareInterestLoading] = useState(false);
  const [interestRequestPhone, setInterestRequestPhone] = useState('');
  const [sharePhoneError, setSharePhoneError] = useState(null);
  const [ndaFile, setNdaFile] = useState(null);
  const [cessionFile, setCessionFile] = useState(null);
  const [submittingNda, setSubmittingNda] = useState(false);
  const [submittingCession, setSubmittingCession] = useState(false);
  const [cedantShares, setCedantShares] = useState([]);
  const [shareInterestsByToken, setShareInterestsByToken] = useState({});
  const [brokerOwnedShares, setBrokerOwnedShares] = useState([]);
  const [brokerShareInterestsByToken, setBrokerShareInterestsByToken] = useState({});
  const [finalizingCession, setFinalizingCession] = useState(null);
  const [lenderOffersForThisProperty, setLenderOffersForThisProperty] = useState([]);
  const [privateLenderOffers, setPrivateLenderOffers] = useState([]);
  const [createOfferModalOpen, setCreateOfferModalOpen] = useState(false);
  const [rejectingOfferId, setRejectingOfferId] = useState(null);
  const [acceptingOfferId, setAcceptingOfferId] = useState(null);
  const [signedContractFile, setSignedContractFile] = useState(null);
  const [submittingAcceptOfferId, setSubmittingAcceptOfferId] = useState(null);
  const [cancelOfferConfirmId, setCancelOfferConfirmId] = useState(null);
  const [cancellingContractOfferId, setCancellingContractOfferId] = useState(null);
  const [summarizingOfferId, setSummarizingOfferId] = useState(null);
  const [expandedSummaryOfferIds, setExpandedSummaryOfferIds] = useState(() => new Set());
  const [offerSummaryCache, setOfferSummaryCache] = useState({});
  const [ownerInterestPayments, setOwnerInterestPayments] = useState({});
  const [ownerDealsTranches, setOwnerDealsTranches] = useState({});
  const [selectedDealForManagement, setSelectedDealForManagement] = useState(null);
  const [selectedDealTranches, setSelectedDealTranches] = useState([]);
  const [ownerDealInterestPayments, setOwnerDealInterestPayments] = useState([]);
  const [lenderInterestAnchorByUid, setLenderInterestAnchorByUid] = useState({});
  const [shareParticipantMembers, setShareParticipantMembers] = useState([]);
  const [ownerBroadcastText, setOwnerBroadcastText] = useState('');
  const [ownerBroadcastBusy, setOwnerBroadcastBusy] = useState(false);
  const [ownerBroadcastNotice, setOwnerBroadcastNotice] = useState(null);
  const [directChatModal, setDirectChatModal] = useState(null);
  const [ownerRecipientsPanelExpanded, setOwnerRecipientsPanelExpanded] = useState(true);
  const [dealRecipientsExpanded, setDealRecipientsExpanded] = useState(false);
  const [revokedRecipients, setRevokedRecipients] = useState([]);
  const [revokedBrokerRows, setRevokedBrokerRows] = useState([]);
  const [recipientSectionError, setRecipientSectionError] = useState(null);
  const [recipientBusyUid, setRecipientBusyUid] = useState(null);
  const [brokerPublicByUid, setBrokerPublicByUid] = useState({});
  /** Courtier / pro (sauf prêteur privé) : pas de révocation tableau de bord si dossier clients lié au propriétaire. */
  const [brokerCourtierRevokeBlocked, setBrokerCourtierRevokeBlocked] = useState({});
  const isPropertyOwner = currentUser?.uid === property?.uid;
  const canEditRentRegister = isPropertyOwner && !shareToken && !!onSaveRentRegister;
  const showRentRegisterButton = !!shareToken || !!onSaveRentRegister;
  const isBrokerViewingClientProperty =
    property?.brokerUid === currentUser?.uid && property?.uid !== currentUser?.uid;
  const isSharedRecipient = !!shareToken && !isPropertyOwner && currentUser?.uid;
  const isBrokerSharedDeal = shareData?.sharedByBroker === true;
  const cessionStyleInterestFlow = shareData?.openToCession === true;
  const ownedDiligenceFlow = shareData?.ownedDiligence === true;
  const brokerMandatedSaleInterestFlow =
    shareData?.brokerMandatedSale === true && !cessionStyleInterestFlow;
  const pocketBrokerContactFlow =
    !!shareData?.pocketDealMode &&
    !!shareData?.sharedByBroker &&
    !cessionStyleInterestFlow &&
    !brokerMandatedSaleInterestFlow &&
    !ownedDiligenceFlow;
  const usePhoneInterestRequest =
    pocketBrokerContactFlow ||
    (brokerMandatedSaleInterestFlow && !!shareData?.pocketDealMode && !!isBrokerSharedDeal);
  const showShareInterestSection =
    isSharedRecipient &&
    (cessionStyleInterestFlow ||
      pocketBrokerContactFlow ||
      brokerMandatedSaleInterestFlow ||
      ownedDiligenceFlow);
  const ndaUploadInterestFlow = cessionStyleInterestFlow || ownedDiligenceFlow;
  const profilePhoneRaw = userProfile?.personalPhone ?? userProfile?.phone ?? '';
  const hasProfilePhoneForInterest = phoneDigitsOnly(profilePhoneRaw).length >= 10;

  const interestPhoneValid = (raw) => {
    const d = String(raw || '').replace(/\D/g, '');
    return d.length >= 10;
  };

  const closeLoanManagementModal = useCallback(() => setSelectedDealForManagement(null), []);
  useRegisterTopEscapeClose(closeLoanManagementModal, !!selectedDealForManagement);

  useEffect(() => {
    if (!property?.id || !property?.uid || !isPropertyOwner) return;
    return subscribeFinancementDossiersForProperty(property.id, property.uid, setFinancementDossiers);
  }, [property?.id, property?.uid, isPropertyOwner]);

  useEffect(() => {
    if (!property?.id || !currentUser?.uid || !isBrokerViewingClientProperty) return;
    return subscribeFinancementDossiersByPropertyAndBroker(
      property.id,
      currentUser.uid,
      setBrokerDossiersForThisProperty
    );
  }, [property?.id, currentUser?.uid, isBrokerViewingClientProperty]);

  useEffect(() => {
    if (!property?.id || !currentUser?.uid || !isBrokerViewingClientProperty || !isPreteurPrive) return;
    return subscribePrivateLenderOffersByPropertyAndLender(
      property.id,
      currentUser.uid,
      setLenderOffersForThisProperty
    );
  }, [property?.id, currentUser?.uid, isBrokerViewingClientProperty, isPreteurPrive]);

  useEffect(() => {
    if (!property?.id || !property?.uid || !isPropertyOwner) return;
    return subscribePrivateLenderOffersForProperty(property.id, property.uid, setPrivateLenderOffers);
  }, [property?.id, property?.uid, isPropertyOwner]);

  useEffect(() => {
    if (!shareToken || !currentUser?.uid) return;
    setShareInterestLoading(true);
    getInterestForRecipient(shareToken, currentUser.uid)
      .then((interest) => {
        setShareInterest(interest || null);
      })
      .catch(() => setShareInterest(null))
      .finally(() => setShareInterestLoading(false));
  }, [shareToken, currentUser?.uid]);

  useEffect(() => {
    if (!isPropertyOwner || !currentUser?.uid || !property?.id) {
      setCedantShares([]);
      return undefined;
    }
    const unsub = subscribeSharesByProperty(currentUser.uid, property.id, setCedantShares);
    return () => unsub?.();
  }, [isPropertyOwner, currentUser?.uid, property?.id]);

  useEffect(() => {
    if (!isPropertyOwner || !property) {
      setBrokerPublicByUid({});
      setBrokerCourtierRevokeBlocked({});
      return undefined;
    }
    const active = getPropertyBrokerAccessUids(property).filter((u) => u && u !== property.uid);
    const revokedIds = (revokedBrokerRows || []).map((r) => r.id).filter((u) => u && u !== property.uid);
    const fromShareParticipants = (shareParticipantMembers || [])
      .map((m) => m?.uid)
      .filter((u) => u && u !== property.uid);
    const fromRevokedShareRecipients = (revokedRecipients || [])
      .map((r) => r?.id)
      .filter((u) => u && u !== property.uid);
    const brokerUids = [
      ...new Set([...active, ...revokedIds, ...fromShareParticipants, ...fromRevokedShareRecipients]),
    ];
    if (!brokerUids.length) {
      setBrokerPublicByUid({});
      setBrokerCourtierRevokeBlocked({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      const nextBlocked = {};
      await Promise.all(
        brokerUids.map(async (uid) => {
          try {
            next[uid] = await getBrokerPublicProfile(uid);
          } catch {
            next[uid] = { displayName: '', persona: null };
          }
          const persona = next[uid]?.persona;
          if (persona !== 'courtier_hypo' && persona !== 'courtier_immo') return;
          const clientId = getPropertyBrokerClientId(property, uid);
          if (!clientId) return;
          try {
            const c = await getClient(clientId);
            if (c && c.uid === uid && c.clientUserId === property.uid) {
              nextBlocked[uid] = true;
            }
          } catch {
            /* ignore */
          }
        }),
      );
      if (!cancelled) {
        setBrokerPublicByUid(next);
        setBrokerCourtierRevokeBlocked(nextBlocked);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isPropertyOwner,
    property?.id,
    property?.uid,
    property?.brokerUid,
    property?.sharedWithBrokerUids,
    property?.sharedWithBrokers,
    property?.clientId,
    revokedBrokerRows,
    shareParticipantMembers,
    revokedRecipients,
  ]);

  useEffect(() => {
    if (!cedantShares.length) return;
    const unsubs = cedantShares.map((share) =>
      subscribeInterests(share.id, (interests) => {
        setShareInterestsByToken((prev) => ({ ...prev, [share.id]: interests }));
      })
    );
    return () => unsubs.forEach((u) => u());
  }, [cedantShares]);

  useEffect(() => {
    if (!isBrokerViewingClientProperty || !currentUser?.uid || !property?.id) {
      setBrokerOwnedShares([]);
      return undefined;
    }
    const unsub = subscribeSharesByProperty(currentUser.uid, property.id, setBrokerOwnedShares);
    return () => unsub?.();
  }, [isBrokerViewingClientProperty, currentUser?.uid, property?.id]);

  useEffect(() => {
    if (!brokerOwnedShares.length) {
      setBrokerShareInterestsByToken({});
      return undefined;
    }
    const unsubs = brokerOwnedShares.map((share) =>
      subscribeInterests(share.id, (interests) => {
        setBrokerShareInterestsByToken((prev) => ({ ...prev, [share.id]: interests }));
      })
    );
    return () => unsubs.forEach((u) => u());
  }, [brokerOwnedShares]);

  const cedantShareIdsKey = useMemo(
    () => cedantShares.map((s) => s.id).filter(Boolean).sort().join('|'),
    [cedantShares],
  );

  useEffect(() => {
    if (!property?.id || !isPropertyOwner) {
      setShareParticipantMembers([]);
      return undefined;
    }
    const tokens = cedantShares.map((s) => s.id).filter(Boolean);
    return subscribeAggregatedShareParticipants(
      tokens,
      setShareParticipantMembers,
      () => setShareParticipantMembers([]),
    );
  }, [property?.id, isPropertyOwner, cedantShareIdsKey]);

  useEffect(() => {
    if (!property?.id || !isPropertyOwner || shareToken) {
      setRevokedRecipients([]);
      return undefined;
    }
    return subscribeShareRevokedRecipients(property.id, setRevokedRecipients, () => setRevokedRecipients([]));
  }, [property?.id, isPropertyOwner, shareToken]);

  useEffect(() => {
    if (!property?.id || !isPropertyOwner || shareToken) {
      setRevokedBrokerRows([]);
      return undefined;
    }
    return subscribeBrokerAccessRevoked(property.id, setRevokedBrokerRows);
  }, [property?.id, isPropertyOwner, shareToken]);

  const dealsOwnerKey = dealsForThisPropertyAsOwner.map((d) => `${d.id}:${d.lenderUid || ''}`).join('|');

  useEffect(() => {
    const deals = dealsForThisPropertyAsOwner || [];
    const uids = [...new Set(deals.map((d) => d.lenderUid).filter(Boolean))];
    if (uids.length === 0) {
      setLenderInterestAnchorByUid({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        uids.map(async (uid) => {
          try {
            const ls = await getLenderSettings(uid);
            next[uid] = normalizeInterestPeriodAnchorDay(ls?.interestPeriodAnchorDay);
          } catch (_) {
            next[uid] = 1;
          }
        })
      );
      if (!cancelled) setLenderInterestAnchorByUid(next);
    })();
    return () => { cancelled = true; };
  }, [dealsOwnerKey]);

  useEffect(() => {
    if (!dealsForThisPropertyAsOwner?.length) {
      setOwnerInterestPayments({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      const next = {};
      for (const deal of dealsForThisPropertyAsOwner) {
        if (cancelled) return;
        const anchor = lenderInterestAnchorByUid[deal.lenderUid] ?? 1;
        const { periodKey } = getCurrentPeriod({ interestPeriodAnchorDay: anchor });
        const payment = await getInterestPayment(deal.id, periodKey, anchor);
        if (!cancelled) next[deal.id] = payment;
      }
      if (!cancelled) setOwnerInterestPayments(next);
    };
    load();
    return () => { cancelled = true; };
  }, [property?.id, dealsOwnerKey, lenderInterestAnchorByUid]);

  useEffect(() => {
    if (!dealsForThisPropertyAsOwner?.length) {
      setOwnerDealsTranches({});
      return;
    }
    let cancelled = false;
    const load = async () => {
      const next = {};
      for (const deal of dealsForThisPropertyAsOwner) {
        if (cancelled) return;
        const tranches = await getFinancedDealTranchesByDeal(deal.id);
        if (!cancelled) next[deal.id] = tranches;
      }
      if (!cancelled) setOwnerDealsTranches(next);
    };
    load();
    return () => { cancelled = true; };
  }, [dealsForThisPropertyAsOwner.map((d) => d.id).join(',')]);

  useEffect(() => {
    if (!selectedDealForManagement?.id) {
      setOwnerDealInterestPayments([]);
      setSelectedDealTranches([]);
      return;
    }
    let cancelled = false;
    getInterestPaymentsForDeal(selectedDealForManagement.id).then((list) => {
      if (!cancelled) setOwnerDealInterestPayments(list);
    });
    getFinancedDealTranchesByDeal(selectedDealForManagement.id).then((tranches) => {
      if (!cancelled) setSelectedDealTranches(tranches);
    });
    return () => { cancelled = true; };
  }, [selectedDealForManagement?.id]);

  // Owner can edit/delete any scenario; broker viewing client property only their own. Share-created scenarios are not editable (form saves to property only) but owner can delete them.
  const canEditScenarioFn = (sc) =>
    !sc.shareToken && (isPropertyOwner || (isBrokerViewingClientProperty && sc.createdByUid === currentUser?.uid));
  const canDeleteScenarioFn = (sc) =>
    isPropertyOwner || (isBrokerViewingClientProperty && !sc.shareToken && sc.createdByUid === currentUser?.uid);

  const formatMoney = (value) =>
    new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatMoneyDecimals = (value) =>
    new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);

  const formatDateShort = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return '—';
    const d = dateStr.slice(0, 10);
    if (!d) return '—';
    try {
      const dateLocale = language === 'fr' ? 'fr-CA' : 'en-CA';
      return new Date(d + 'T12:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  const formatPercent = (value) =>
    value || value === 0 ? `${value.toFixed(1)}%` : '—';

  const getOfferDisplayLabel = (offer) => {
    const dateStr = (offer.createdAt?.toDate ? offer.createdAt.toDate() : new Date(offer.createdAt)).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const comment = offer.comment?.trim();
    const fileName = offer.contractFileRef?.name;
    const prefix = comment
      ? (comment.length > 60 ? comment.slice(0, 60) + '…' : comment)
      : (fileName || '');
    return prefix ? `${prefix} – ${dateStr}` : dateStr;
  };

  const sortOffersByCreatedAtAsc = (list) =>
    [...(list || [])].sort((a, b) => {
      const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      return ta - tb;
    });

  const pricePerDoorValue =
    property.numberOfUnits && property.purchasePrice
      ? Number(property.purchasePrice) / Number(property.numberOfUnits)
      : null;

  const ownedDeal = isOwnedDeal(property);
  const ownedAnalysis = useMemo(() => {
    if (!ownedDeal || !property) return null;
    return calculateRentability(
      {
        ...property,
        ...(property.financingType
          ? {}
          : { financingType: 'existing' }),
      },
      property.advancedExpenses,
    );
  }, [ownedDeal, property]);

  const stats = [
    {
      label: ownedDeal ? t('building.estimatedMarketValue') : t('building.purchasePrice'),
      value: formatMoney(Number(property.purchasePrice)),
      icon: <DollarSign className="w-6 h-6 text-emerald-600" />,
    },
    ...(ownedDeal
      ? [
          {
            label: t('building.netEquity'),
            value: formatMoney(ownedAnalysis?.equity ?? 0),
            icon: <TrendingUp className="w-6 h-6 text-teal-600" />,
          },
          {
            label: t('building.ltv'),
            value:
              ownedAnalysis?.loanValueRatio != null
                ? `${Number(ownedAnalysis.loanValueRatio).toFixed(1)}%`
                : '—',
            icon: <BarChart className="w-6 h-6 text-cyan-600" />,
          },
        ]
      : []),
    {
      label: t('building.numberOfUnits'),
      value: property.numberOfUnits || '—',
      icon: <Building className="w-6 h-6 text-gray-600" />,
    },
    {
      label: t('building.pricePerDoor'),
      value: pricePerDoorValue ? formatMoney(pricePerDoorValue) : 'N/A',
      icon: <DollarSign className="w-6 h-6 text-indigo-600" />,
    },
    {
      label: t('building.grossIncome'),
      value: formatMoney(property.totalGrossRevenue),
      icon: <DollarSign className="w-6 h-6 text-green-600" />,
    },
    {
      label: t('building.totalExpenses'),
      value: formatMoney(property.totalExpenses),
      icon: <DollarSign className="w-6 h-6 text-red-600" />,
    },
    {
      label: t('building.netIncome'),
      value: formatMoney(property.netOperatingIncome),
      icon: <DollarSign className="w-6 h-6 text-purple-600" />,
    },
    {
      label: t('building.grm'),
      value: property.grossRentMultiplier
        ? property.grossRentMultiplier.toFixed(1)
        : '—',
      icon: <BarChart className="w-6 h-6 text-blue-600" />,
    },
    {
      label: t('building.nim'),
      value: property.netIncomeMultiplier
        ? property.netIncomeMultiplier.toFixed(1)
        : '—',
      icon: <BarChart className="w-6 h-6 text-purple-600" />,
    },
    {
      label: t('building.capRate'),
      value: formatPercent(property.capRate),
      icon: <TrendingUp className="w-6 h-6 text-orange-600" />,
    },
  ];

  const fullAddress = [
    property.address,
    property.city,
    property.province,
    property.postalCode,
  ]
    .filter(Boolean)
    .join(', ');

  const scenarioTypeLabels = {
    initialFinancing: t('scenario.initialFinancing'),
    refinancing: t('scenario.refinancing'),
    renewal: t('scenario.renewal'),
    optimization: t('scenario.optimization'),
    other: t('scenario.other'),
  };

  const dealChatUser = useMemo(() => {
    if (!currentUser) return null;
    return {
      ...currentUser,
      displayName: userProfile?.displayName || currentUser.displayName,
    };
  }, [currentUser, userProfile]);

  const ownerShareIdsSet = useMemo(
    () => new Set(cedantShares.map((s) => s.id).filter(Boolean)),
    [cedantShares],
  );

  const dealRecipientRows = useMemo(() => {
    const revokedMap = Object.fromEntries(revokedRecipients.map((r) => [r.id, r]));
    const rows = new Map();

    for (const m of shareParticipantMembers) {
      if (!m?.uid || m.uid === property?.uid) continue;
      const rowTokens = Array.isArray(m.shareTokens) ? [...m.shareTokens] : [];
      const hasLink =
        rowTokens.some((tok) => ownerShareIdsSet.has(tok)) ||
        cedantShares.length > 0 ||
        rowTokens.length > 0;
      if (!hasLink) continue;
      const pub = brokerPublicByUid[m.uid] || { displayName: '', persona: null };
      rows.set(m.uid, {
        uid: m.uid,
        displayName: (m.displayName || pub.displayName || '').trim(),
        shareTokens: rowTokens,
        revoked: !!revokedMap[m.uid],
        shareLinkRecipient: true,
        brokerAccess: false,
        brokerPersona: pub.persona ?? null,
        brokerProfessionalRevoked: false,
      });
    }

    for (const rev of revokedRecipients) {
      const uid = rev.id;
      if (!uid || uid === property?.uid) continue;
      const tokens = Array.isArray(rev.shareTokens) ? rev.shareTokens.filter(Boolean) : [];
      const existing = rows.get(uid);
      const pubRev = brokerPublicByUid[uid] || { displayName: '', persona: null };
      if (existing) {
        rows.set(uid, {
          ...existing,
          revoked: true,
          shareTokens: [...new Set([...existing.shareTokens, ...tokens])],
          displayName: existing.displayName || (rev.displayName || '').trim() || pubRev.displayName || '',
          shareLinkRecipient: true,
          brokerPersona: existing.brokerPersona ?? pubRev.persona ?? null,
          brokerProfessionalRevoked: existing.brokerProfessionalRevoked ?? false,
        });
      } else {
        rows.set(uid, {
          uid,
          displayName: (rev.displayName || pubRev.displayName || '').trim(),
          shareTokens: tokens,
          revoked: true,
          shareLinkRecipient: true,
          brokerAccess: false,
          brokerPersona: pubRev.persona ?? null,
          brokerProfessionalRevoked: false,
        });
      }
    }

    const brokerUids = getPropertyBrokerAccessUids(property).filter((u) => u && u !== property?.uid);
    for (const uid of brokerUids) {
      const pub = brokerPublicByUid[uid] || { displayName: '', persona: null };
      const existing = rows.get(uid);
      if (existing) {
        rows.set(uid, {
          ...existing,
          brokerAccess: true,
          brokerProfessionalRevoked: false,
          brokerPersona: pub.persona ?? existing.brokerPersona ?? null,
          displayName: (existing.displayName || pub.displayName || '').trim(),
        });
      } else {
        rows.set(uid, {
          uid,
          displayName: (pub.displayName || '').trim(),
          shareTokens: [],
          revoked: false,
          shareLinkRecipient: false,
          brokerAccess: true,
          brokerPersona: pub.persona,
          brokerProfessionalRevoked: false,
        });
      }
    }

    for (const revB of revokedBrokerRows) {
      const uid = revB?.id;
      if (!uid || uid === property?.uid) continue;
      if (propertyHasActiveBrokerDashboardShare(property, uid)) continue;
      const pub = brokerPublicByUid[uid] || { displayName: '', persona: null };
      const storedName =
        typeof revB.displayName === 'string' && revB.displayName.trim() ? revB.displayName.trim() : '';
      const dn = storedName || pub.displayName || '';
      const existing = rows.get(uid);
      if (existing) {
        rows.set(uid, {
          ...existing,
          brokerAccess: true,
          brokerProfessionalRevoked: true,
          brokerPersona: pub.persona ?? existing.brokerPersona ?? null,
          displayName: (existing.displayName || dn || pub.displayName || '').trim(),
        });
      } else {
        rows.set(uid, {
          uid,
          displayName: (dn || pub.displayName || '').trim(),
          shareTokens: [],
          revoked: false,
          shareLinkRecipient: false,
          brokerAccess: true,
          brokerPersona: pub.persona,
          brokerProfessionalRevoked: true,
        });
      }
    }

    return Array.from(rows.values())
      .map((r) => ({
        ...r,
        label: r.displayName?.trim() || `${r.uid.slice(0, 8)}…`,
        shareTokens: [...new Set((r.shareTokens || []).filter(Boolean))],
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [
    shareParticipantMembers,
    revokedRecipients,
    property?.uid,
    property?.brokerUid,
    property?.sharedWithBrokerUids,
    brokerPublicByUid,
    cedantShares,
    ownerShareIdsSet,
    revokedBrokerRows,
  ]);

  const activeDealRecipientRows = useMemo(
    () => dealRecipientRows.filter((r) => !r.revoked),
    [dealRecipientRows],
  );

  const broadcastEligibleRows = useMemo(() => {
    const fallbackTokens = cedantShares.map((s) => s.id).filter(Boolean);
    return activeDealRecipientRows.filter((r) => {
      if (!r.shareLinkRecipient) return false;
      const rowToks = (r.shareTokens || []).filter(Boolean);
      if (rowToks.some((t) => ownerShareIdsSet.has(t))) return true;
      if (rowToks.length > 0 && fallbackTokens.length > 0) return true;
      return false;
    });
  }, [activeDealRecipientRows, ownerShareIdsSet, cedantShares]);

  /** Partages deal encore présents chez au moins un destinataire (recipientCount maintenu par Cloud Functions). */
  const hasActiveShareRecipients = useMemo(
    () => cedantShares.some((s) => Number(s.recipientCount ?? 0) > 0),
    [cedantShares],
  );

  const hasBrokerSharesOnProperty = useMemo(() => {
    const hasActiveShare = getPropertyBrokerAccessUids(property).some(
      (u) => u && u !== property?.uid && propertyHasActiveBrokerDashboardShare(property, u),
    );
    if (hasActiveShare) return true;
    return (revokedBrokerRows || []).some(
      (r) =>
        r?.id &&
        r.id !== property?.uid &&
        !propertyHasActiveBrokerDashboardShare(property, r.id),
    );
  }, [
    property,
    property?.uid,
    property?.brokerUid,
    property?.sharedWithBrokerUids,
    property?.sharedWithBrokers,
    property?.clientId,
    revokedBrokerRows,
  ]);

  const showOwnerDealRecipients =
    !shareToken &&
    isPropertyOwner &&
    !!currentUser &&
    !!property?.id &&
    ((cedantShares.length > 0 && hasActiveShareRecipients) || hasBrokerSharesOnProperty);

  const dealOwnerUidForSharedChat = shareData?.uid ?? property?.uid;
  const showMessageDealOwnerInHeader =
    isSharedRecipient &&
    shareToken &&
    currentUser?.uid &&
    dealOwnerUidForSharedChat &&
    dealOwnerUidForSharedChat !== currentUser.uid;

  /** Courtier / prêteur sur immeuble client ou financé : DM broker↔propriétaire via dossier clients. */
  const brokerViewClientIdForOwnerChat =
    !shareToken && currentUser?.uid && property?.uid && property.uid !== currentUser.uid
      ? getPropertyBrokerClientId(property, currentUser.uid)
      : '';
  const showMessageDealOwnerForBrokerView = Boolean(brokerViewClientIdForOwnerChat);

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 overflow-x-hidden">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
          {shareAccessBannerText ? (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
              <p className="text-sm font-medium text-amber-900">{shareAccessBannerText}</p>
            </div>
          ) : null}
          <div data-onboarding="dashboard-overview">
          <div className="flex flex-col gap-3 mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-semibold flex flex-wrap items-center gap-2">
                  {pocketListingLayout ? t('pocketListing.workspaceSectionTitle') : t('building.dashboard.title')}
                  {ownedDeal && (
                    <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-800">
                      {t('dealPurpose.badge.owned')}
                    </span>
                  )}
                </h2>
                {!pocketListingLayout ? (
                  <h3 className="text-base sm:text-xl font-semibold mt-2 break-words">
                    {fullAddress || t('home.address.unset')}
                  </h3>
                ) : null}
              </div>
              {((onEditProperty && !readOnly) ||
                onBack ||
                onRemoveFromSharedDashboard ||
                showMessageDealOwnerInHeader ||
                showMessageDealOwnerForBrokerView) && (
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto sm:justify-end shrink-0">
                  {(showMessageDealOwnerInHeader || showMessageDealOwnerForBrokerView) && (
                    <button
                      type="button"
                      onClick={() => {
                        if (showMessageDealOwnerInHeader) {
                          const spec = buildShareRecipientConversationSpec(
                            shareToken,
                            currentUser.uid,
                            dealOwnerUidForSharedChat,
                          );
                          setDirectChatModal({
                            conversationId: spec.conversationId,
                            ensureSpec: {
                              participantUids: spec.participantUids,
                              kind: spec.kind,
                              context: spec.context,
                            },
                            title: t('directChat.messageOwner'),
                            subtitle: fullAddress,
                          });
                          return;
                        }
                        const spec = buildBrokerClientConversationSpec(
                          brokerViewClientIdForOwnerChat,
                          currentUser.uid,
                          property.uid,
                        );
                        setDirectChatModal({
                          conversationId: spec.conversationId,
                          ensureSpec: {
                            participantUids: spec.participantUids,
                            kind: spec.kind,
                            context: spec.context,
                          },
                          title: t('directChat.messageOwner'),
                          subtitle: fullAddress,
                        });
                      }}
                      className="p-2 rounded-lg text-violet-700 border border-violet-200/80 hover:bg-violet-50"
                      title={t('directChat.messageOwner')}
                      aria-label={t('directChat.messageOwner')}
                    >
                      <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" aria-hidden />
                    </button>
                  )}
                  {onEditProperty && !readOnly && (
                    <button
                      type="button"
                      data-onboarding="dashboard-edit"
                      onClick={onEditProperty}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base text-gray-900"
                    >
                      {t('edit')}
                    </button>
                  )}
                  {onRemoveFromSharedDashboard && (
                    <button
                      type="button"
                      onClick={onRemoveFromSharedDashboard}
                      className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 text-sm sm:text-base"
                    >
                      {t('sharedView.removeProperty')}
                    </button>
                  )}
                  {onBack && (
                    <button
                      type="button"
                      onClick={onBack}
                      className="text-gray-600 hover:text-gray-800 text-sm sm:text-base font-medium"
                    >
                      ← {t('back')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {!pocketListingLayout ? (
            <div className="mb-6">
              {isCourtierHypo && property.fromClient && property.clientId && clients.length > 0 && (() => {
                const client = clients.find((c) => c.id === property.clientId);
                if (!client) return null;
                return (
                  <p className="mt-2 text-sm text-gray-600">
                    {t('building.propertyOfClient')} {client.name || client.email || client.id}
                  </p>
                );
              })()}
            </div>
          ) : null}

          {pocketListingLayout ? (
            <PocketDealListingHero
              property={property}
              advancedExpenses={!!property.advancedExpenses}
            />
          ) : null}

          {!pocketListingLayout ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {stats.map(({ label, value, icon }) => (
                <div
                  key={label}
                  className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200"
                >
                  {icon}
                  <div>
                    <p className="text-sm text-gray-500">{label}</p>
                    <p className="text-lg font-semibold">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          </div>

          {showOwnerDealRecipients && currentUser?.uid && property?.uid && (
            <div className="mb-6 border border-indigo-100 rounded-xl bg-indigo-50/40 overflow-hidden">
              <button
                type="button"
                onClick={() => setOwnerRecipientsPanelExpanded((ex) => !ex)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-indigo-50/80 transition-colors"
              >
                <span className="flex items-center gap-2 font-medium text-indigo-950 text-sm min-w-0">
                  <UsersRound className="w-5 h-5 text-indigo-600 shrink-0" aria-hidden />
                  <Megaphone className="w-5 h-5 text-indigo-600 shrink-0" aria-hidden />
                  <span className="truncate">{t('building.recipientsBroadcastPanelTitle')}</span>
                </span>
                <span className="text-indigo-700 text-xs font-medium shrink-0">
                  {ownerRecipientsPanelExpanded ? '▼' : '▶'}
                </span>
              </button>
              {ownerRecipientsPanelExpanded && (
                <div className="px-4 pb-4 space-y-5 border-t border-indigo-100/60 pt-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 font-medium text-indigo-950 text-sm">
                      <Megaphone className="w-5 h-5 text-indigo-600 shrink-0" aria-hidden />
                      {t('building.ownerBroadcastTitle')}
                    </div>
                    <p className="text-xs text-indigo-900/85 leading-snug">{t('building.ownerBroadcastHint')}</p>
                    {ownerBroadcastNotice ? (
                      <p
                        className={`text-sm ${ownerBroadcastNotice.kind === 'error' ? 'text-red-600' : 'text-emerald-800'}`}
                        role="status"
                      >
                        {ownerBroadcastNotice.message}
                      </p>
                    ) : null}
                    <textarea
                      value={ownerBroadcastText}
                      onChange={(e) => {
                        setOwnerBroadcastText(e.target.value);
                        if (ownerBroadcastNotice) setOwnerBroadcastNotice(null);
                      }}
                      placeholder={t('building.ownerBroadcastPlaceholder')}
                      rows={3}
                      maxLength={8000}
                      disabled={ownerBroadcastBusy}
                      className="w-full text-sm border border-indigo-200 rounded-lg px-3 py-2 resize-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 bg-white"
                    />
                    <button
                      type="button"
                      disabled={
                        ownerBroadcastBusy
                        || !ownerBroadcastText.trim()
                        || broadcastEligibleRows.length === 0
                      }
                      onClick={async () => {
                        if (!broadcastEligibleRows.length || !property.uid) return;
                        setOwnerBroadcastBusy(true);
                        setOwnerBroadcastNotice(null);
                        const displayName =
                          userProfile?.displayName?.trim()
                          || currentUser?.displayName?.trim()
                          || currentUser?.email?.split('@')[0]
                          || property.uid.slice(0, 8);
                        try {
                          const { sent, failed } = await broadcastOwnerMessageToShareRecipients({
                            ownerUid: property.uid,
                            ownerDisplayName: displayName,
                            text: ownerBroadcastText,
                            recipients: broadcastEligibleRows,
                            propertyShareTokens: cedantShares.map((s) => s.id).filter(Boolean),
                          });
                          if (sent === 0 && failed === 0) {
                            setOwnerBroadcastNotice({
                              kind: 'error',
                              message: t('building.ownerBroadcastNoRecipients'),
                            });
                          } else if (failed > 0) {
                            setOwnerBroadcastNotice({
                              kind: sent > 0 ? 'ok' : 'error',
                              message: t('building.ownerBroadcastPartial')
                                .replace('{sent}', String(sent))
                                .replace('{fail}', String(failed)),
                            });
                          } else {
                            setOwnerBroadcastNotice({
                              kind: 'ok',
                              message: t('building.ownerBroadcastSuccess').replace('{count}', String(sent)),
                            });
                            setOwnerBroadcastText('');
                          }
                        } catch (e) {
                          console.warn('owner broadcast', e);
                          setOwnerBroadcastNotice({ kind: 'error', message: t('building.ownerBroadcastError') });
                        } finally {
                          setOwnerBroadcastBusy(false);
                        }
                      }}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-45 disabled:pointer-events-none"
                    >
                      {ownerBroadcastBusy ? (
                        <Loader2 className="w-5 h-5 animate-spin shrink-0" aria-hidden />
                      ) : null}
                      {ownerBroadcastBusy ? t('building.ownerBroadcastSending') : t('building.ownerBroadcastSend')}
                    </button>
                  </div>

                  <div className="border border-violet-200/80 rounded-xl bg-violet-50/50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setDealRecipientsExpanded((ex) => !ex)}
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-violet-50/90 transition-colors"
                    >
                      <span className="flex items-center gap-2 font-medium text-violet-950 text-sm">
                        <UsersRound className="w-5 h-5 text-violet-600 shrink-0" aria-hidden />
                        {t('building.dealRecipientsSection')}
                      </span>
                      <span className="text-violet-700 text-xs font-medium">{dealRecipientsExpanded ? '▼' : '▶'}</span>
                    </button>
                    {dealRecipientsExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-violet-100/80">
                        <p className="text-xs text-violet-900/85 leading-snug pt-2">{t('building.dealRecipientsHint')}</p>
                        {recipientSectionError ? (
                          <p className="text-sm text-red-600" role="alert">
                            {recipientSectionError}
                          </p>
                        ) : null}
                        {dealRecipientRows.length === 0 ? (
                          <p className="text-sm text-violet-900/80">{t('building.dealRecipientsEmpty')}</p>
                        ) : (
                          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {dealRecipientRows.map((row) => {
                              const busy = recipientBusyUid === row.uid;
                              return (
                                <li
                                  key={row.uid}
                                  className="flex flex-col gap-2 py-2 px-3 bg-white/90 border border-violet-100 rounded-lg min-w-0"
                                >
                                  <div className="flex items-start justify-between gap-2 min-w-0">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm text-violet-950 font-medium truncate" title={row.label}>
                                        {row.label}
                                      </div>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {row.shareLinkRecipient ? (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-sky-100 text-sky-900 leading-snug">
                                            {t(recipientLinkShareBadgeKey(row.brokerPersona))}
                                          </span>
                                        ) : null}
                                        {row.brokerAccess &&
                                        (row.brokerProfessionalRevoked ||
                                          !row.shareLinkRecipient ||
                                          !row.brokerPersona) ? (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-950 leading-snug">
                                            {row.brokerProfessionalRevoked
                                              ? t('building.recipientBadgeProfessionalRevoked')
                                              : t(recipientProfessionalBadgeKey(row.brokerPersona))}
                                          </span>
                                        ) : null}
                                      </div>
                                    </div>
                                    <span className="flex items-center gap-0.5 shrink-0 flex-wrap justify-end">
                                      {row.shareLinkRecipient ? (
                                        <button
                                          type="button"
                                          disabled={busy}
                                          onClick={async () => {
                                            setRecipientSectionError(null);
                                            setRecipientBusyUid(row.uid);
                                            try {
                                              if (row.revoked) {
                                                await restoreDealShareRecipientAccess({
                                                  propertyId: property.id,
                                                  ownerUid: property.uid,
                                                  recipientUid: row.uid,
                                                });
                                              } else {
                                                let tokens = row.shareTokens.filter((t) => ownerShareIdsSet.has(t));
                                                if (tokens.length === 0) {
                                                  tokens = [
                                                    ...new Set([
                                                      ...row.shareTokens,
                                                      ...cedantShares.map((s) => s.id).filter(Boolean),
                                                    ]),
                                                  ];
                                                }
                                                if (tokens.length === 0) {
                                                  setRecipientSectionError(t('directChat.noResolvableShareToken'));
                                                  return;
                                                }
                                                await revokeDealShareRecipientAccess({
                                                  propertyId: property.id,
                                                  recipientUid: row.uid,
                                                  displayName: row.label,
                                                  shareTokens: tokens,
                                                });
                                              }
                                            } catch (e) {
                                              console.warn('deal recipient access', e);
                                              setRecipientSectionError(t('building.recipientActionError'));
                                            } finally {
                                              setRecipientBusyUid(null);
                                            }
                                          }}
                                          className="p-1.5 rounded-md text-violet-800 hover:bg-violet-100 disabled:opacity-45 disabled:pointer-events-none"
                                          title={
                                            row.revoked
                                              ? t('building.restoreRecipientAccess')
                                              : t('building.revokeRecipientAccess')
                                          }
                                          aria-label={
                                            row.revoked
                                              ? t('building.restoreRecipientAccess')
                                              : t('building.revokeRecipientAccess')
                                          }
                                        >
                                          {row.revoked ? (
                                            <UserRoundCheck className="w-5 h-5 shrink-0" aria-hidden />
                                          ) : (
                                            <UserRoundX className="w-5 h-5 shrink-0" aria-hidden />
                                          )}
                                        </button>
                                      ) : null}
                                      {row.brokerAccess ? (
                                        row.brokerProfessionalRevoked ? (
                                          <button
                                            type="button"
                                            disabled={busy}
                                            onClick={async () => {
                                              setRecipientSectionError(null);
                                              setRecipientBusyUid(row.uid);
                                              try {
                                                await restoreBrokerPropertyAccess(property.id, row.uid);
                                              } catch (e) {
                                                console.warn('broker property access', e);
                                                setRecipientSectionError(t('building.recipientActionError'));
                                              } finally {
                                                setRecipientBusyUid(null);
                                              }
                                            }}
                                            className="p-1.5 rounded-md text-amber-900 hover:bg-amber-100 disabled:opacity-45 disabled:pointer-events-none"
                                            title={t('building.restoreBrokerAccess')}
                                            aria-label={t('building.restoreBrokerAccess')}
                                          >
                                            <UserRoundCheck className="w-5 h-5 shrink-0" aria-hidden />
                                          </button>
                                        ) : brokerCourtierRevokeBlocked[row.uid] ? (
                                          <span
                                            className="p-1.5 rounded-md text-amber-800/55 inline-flex"
                                            title={t('building.revokeBrokerBlockedClientRelation')}
                                          >
                                            <Lock
                                              className="w-5 h-5 shrink-0"
                                              aria-hidden
                                            />
                                            <span className="sr-only">
                                              {t('building.revokeBrokerBlockedClientRelation')}
                                            </span>
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            disabled={busy}
                                            onClick={async () => {
                                              if (!window.confirm(t('building.revokeBrokerAccessConfirm'))) return;
                                              setRecipientSectionError(null);
                                              setRecipientBusyUid(row.uid);
                                              try {
                                                await revokeBrokerPropertyDashboardAccess(
                                                  property.id,
                                                  row.uid,
                                                  row.label,
                                                );
                                              } catch (e) {
                                                console.warn('broker property access', e);
                                                setRecipientSectionError(t('building.recipientActionError'));
                                              } finally {
                                                setRecipientBusyUid(null);
                                              }
                                            }}
                                            className="p-1.5 rounded-md text-amber-900 hover:bg-amber-100 disabled:opacity-45 disabled:pointer-events-none"
                                            title={t('building.revokeBrokerAccess')}
                                            aria-label={t('building.revokeBrokerAccess')}
                                          >
                                            <UserRoundX className="w-5 h-5 shrink-0" aria-hidden />
                                          </button>
                                        )
                                      ) : null}
                                      <button
                                        type="button"
                                        disabled={busy || row.revoked || !row.shareLinkRecipient}
                                        onClick={async () => {
                                          if (row.revoked || !row.shareLinkRecipient) return;
                                          setRecipientSectionError(null);
                                          setRecipientBusyUid(row.uid);
                                          try {
                                            const candidates = [
                                              ...row.shareTokens.filter((tok) => ownerShareIdsSet.has(tok)),
                                              ...cedantShares.map((s) => s.id).filter(Boolean),
                                              ...row.shareTokens,
                                            ];
                                            const dmToken = await findShareTokenForDirectChat(
                                              currentUser.uid,
                                              row.uid,
                                              candidates,
                                            );
                                            if (!dmToken) {
                                              setRecipientSectionError(t('directChat.noResolvableShareToken'));
                                              return;
                                            }
                                            const spec = buildShareRecipientConversationSpec(
                                              dmToken,
                                              currentUser.uid,
                                              row.uid,
                                            );
                                            setDirectChatModal({
                                              conversationId: spec.conversationId,
                                              ensureSpec: {
                                                participantUids: spec.participantUids,
                                                kind: spec.kind,
                                                context: spec.context,
                                              },
                                              title: t('directChat.messageRecipient'),
                                              subtitle: row.label,
                                            });
                                          } finally {
                                            setRecipientBusyUid(null);
                                          }
                                        }}
                                        className="p-1.5 rounded-md text-violet-800 hover:bg-violet-100 disabled:opacity-40 disabled:pointer-events-none"
                                        title={
                                          !row.shareLinkRecipient
                                            ? t('building.recipientChatLinkOnly')
                                            : row.revoked
                                              ? t('building.recipientChatDisabled')
                                              : t('building.openRecipientChat')
                                        }
                                        aria-label={
                                          !row.shareLinkRecipient
                                            ? t('building.recipientChatLinkOnly')
                                            : row.revoked
                                              ? t('building.recipientChatDisabled')
                                              : t('building.openRecipientChat')
                                        }
                                      >
                                        <MessageCircle className="w-5 h-5 shrink-0" aria-hidden />
                                      </button>
                                    </span>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {isBrokerViewingClientProperty && !shareToken && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => setRequestDossierModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-sm"
                >
                  <FileCheck className="w-5 h-5" />
                  {t('financementDossier.sendChecklist')}
                </button>
                {isPreteurPrive && (
                  <button
                    type="button"
                    onClick={() => setCreateOfferModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 shadow-sm"
                  >
                    <DollarSign className="w-5 h-5" />
                    {t('privateLenderOffer.makeOffer')}
                  </button>
                )}
              </div>
              {brokerDossiersForThisProperty.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('financementDossier.checklistsSentToClient')}</h4>
                  <ul className="space-y-2">
                  {brokerDossiersForThisProperty.map((d) => {
                    const label = d.checklistName?.trim() || (d.createdAt
                      ? (d.createdAt?.toDate ? d.createdAt.toDate() : new Date(d.createdAt)).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                      : t('financementDossier.requested'));
                    return (
                      <li
                        key={d.id}
                        className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white rounded-lg border border-blue-100"
                      >
                        <span className="text-sm text-gray-800">
                          {label}
                          <span className="ml-2 text-gray-500">{t(`financementDossier.status_${d.status || 'not_started'}`)}</span>
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                          {onOpenFinancementDossier && (
                            <button
                              type="button"
                              onClick={() => onOpenFinancementDossier(d)}
                              className="inline-flex items-center gap-1 px-3 py-2 text-blue-600 hover:bg-blue-100 rounded-lg border border-blue-200 text-sm"
                            >
                              {t('financementDossier.open')}
                            </button>
                          )}
                          {d.status !== 'submitted' && (
                            cancelDossierConfirm === d.id ? (
                              <span className="inline-flex items-center gap-1 text-sm">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    await cancelFinancementDossier(d.id);
                                    setCancelDossierConfirm(null);
                                  }}
                                  className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                                >
                                  {t('confirm')}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setCancelDossierConfirm(null)}
                                  className="px-2 py-1 rounded border hover:bg-gray-100"
                                >
                                  {t('cancel')}
                                </button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setCancelDossierConfirm(d.id)}
                                className="inline-flex items-center gap-1 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-200 text-sm"
                              >
                                {t('financementDossier.cancelRequest')}
                              </button>
                            )
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                </div>
              )}
              {isPreteurPrive && lenderOffersForThisProperty.length > 0 && (
                <div className={brokerDossiersForThisProperty.length > 0 ? 'mt-4 pt-4 border-t border-blue-200' : 'mt-3'}>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">{t('privateLenderOffer.offersForProperty')}</h4>
                  <ul className="space-y-2">
                    {sortOffersByCreatedAtAsc(lenderOffersForThisProperty).map((offer) => (
                      <li
                        key={offer.id}
                        className={`flex flex-col gap-1 p-3 rounded-lg border ${offer.status === 'cancelled' || offer.status === 'deal_cancelled' ? 'bg-gray-50 border-gray-200 opacity-90' : 'bg-white border-emerald-100'}`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                          <span className="text-sm text-gray-800">{getOfferDisplayLabel(offer)}</span>
                          <span className="text-sm text-gray-500 shrink-0">{t(`privateLenderOffer.status_${offer.status || 'pending'}`)}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                            {offer.contractFileRef && offer.status !== 'rejected' && offer.status !== 'cancelled' && offer.status !== 'deal_cancelled' && (
                              <a
                                href="#"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  try {
                                    const url = await getOfferFileDownloadUrl(offer.contractFileRef.storagePath);
                                    window.open(url, '_blank');
                                  } catch (err) {
                                    console.error(err);
                                  }
                                }}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {t('privateLenderOffer.downloadContract')}
                              </a>
                            )}
                            {offer.status === 'accepted' && offer.signedContractFileRef && (
                              <>
                                <a
                                  href="#"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                      const url = await getOfferFileDownloadUrl(offer.signedContractFileRef.storagePath);
                                      window.open(url, '_blank');
                                    } catch (err) {
                                      console.error(err);
                                    }
                                  }}
                                  className="text-xs text-emerald-600 hover:underline"
                                >
                                  {t('privateLenderOffer.downloadSignedContract')}
                                </a>
                                {(() => {
                                  const deal = dealsForThisProperty.find((d) => d.offerId === offer.id);
                                  if (deal && typeof onOpenLoanDetail === 'function') {
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => onOpenLoanDetail(deal)}
                                        className="text-xs text-indigo-600 hover:underline ml-1"
                                      >
                                        {t('privateLenderLoans.detailTitle')}
                                      </button>
                                    );
                                  }
                                  return null;
                                })()}
                                {(() => {
                                  const deal = dealsForThisProperty.find((d) => d.offerId === offer.id);
                                  const canCancel = deal && deal.status !== 'cancelled' && !deal.fundsDisbursedAt && currentUser?.uid === offer.lenderUid;
                                  if (!canCancel) return null;
                                  return (
                                    <button
                                      type="button"
                                      disabled={cancellingContractOfferId === offer.id}
                                      onClick={async () => {
                                        if (!window.confirm(t('privateLenderLoans.cancelDealConfirm'))) return;
                                        setCancellingContractOfferId(offer.id);
                                        try {
                                          await updateFinancedDeal(deal.id, { status: 'cancelled', cancelledAt: serverTimestamp() });
                                          await updatePrivateLenderOffer(offer.id, { status: 'deal_cancelled' });
                                          if (typeof refreshFinancedDeals === 'function') await refreshFinancedDeals();
                                        } catch (err) {
                                          console.error(err);
                                          alert((t('share.error') || 'Error') + ': ' + (err?.message || String(err)));
                                        } finally {
                                          setCancellingContractOfferId(null);
                                        }
                                      }}
                                      className="text-xs text-red-600 hover:underline disabled:opacity-50"
                                    >
                                      {cancellingContractOfferId === offer.id ? '…' : t('privateLenderOffer.cancelContract')}
                                    </button>
                                  );
                                })()}
                              </>
                            )}
                            {offer.status === 'pending' && (
                              cancelOfferConfirmId === offer.id ? (
                                <span className="inline-flex items-center gap-1 text-xs">
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        await cancelPrivateLenderOfferByLender(offer.id);
                                        setCancelOfferConfirmId(null);
                                      } catch (err) {
                                        console.error(err);
                                        alert((t('share.error') || 'Erreur') + ': ' + (err?.message || String(err)));
                                      }
                                    }}
                                    className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                                  >
                                    {t('confirm')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setCancelOfferConfirmId(null)}
                                    className="px-2 py-1 rounded border hover:bg-gray-100"
                                  >
                                    {t('cancel')}
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setCancelOfferConfirmId(offer.id)}
                                  className="text-xs text-red-600 hover:underline"
                                >
                                  {t('privateLenderOffer.cancelOffer')}
                                </button>
                              )
                            )}
                          </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {showShareInterestSection && (
            <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h3 className="font-medium text-indigo-900 mb-3 flex items-center gap-2">
                <Info className="w-5 h-5" />
                {t(isBrokerSharedDeal ? 'shareInterest.sectionTitleBroker' : 'shareInterest.sectionTitle')}
              </h3>
              {shareData?.listingAccessLockedAt &&
              shareData?.winningInterestId &&
              shareInterest?.id &&
              shareInterest.id !== shareData.winningInterestId ? (
                <p className="text-sm text-indigo-900">{t('shareInterest.listingAccessClosed')}</p>
              ) : shareInterestLoading ? (
                <p className="text-sm text-indigo-700">{t('loading')}</p>
              ) : !shareInterest ? (
                usePhoneInterestRequest ? (
                  <div className="space-y-3">
                    {hasProfilePhoneForInterest ? (
                      <p className="text-sm text-indigo-800">{t('shareInterest.pocketContactConsentProfilePhone')}</p>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-indigo-900 mb-1">
                          {t('shareInterest.phoneLabel')}
                        </label>
                        <p className="text-xs text-indigo-700 mb-2">{t('shareInterest.pocketContactConsentWithPhone')}</p>
                        <input
                          type="tel"
                          inputMode="numeric"
                          value={interestRequestPhone}
                          onChange={(e) => {
                            setInterestRequestPhone(formatNorthAmericanPhoneInput(e.target.value));
                            setSharePhoneError(null);
                          }}
                          className="w-full border border-indigo-200 rounded-lg px-3 py-2 text-sm bg-white"
                          placeholder={t('shareInterest.phonePlaceholder')}
                          autoComplete="tel"
                          maxLength={14}
                        />
                        {sharePhoneError && <p className="text-sm text-red-600 mt-1">{sharePhoneError}</p>}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        if (!shareToken || !currentUser?.uid) return;
                        const phoneToSend = hasProfilePhoneForInterest
                          ? (formatNorthAmericanPhoneInput(profilePhoneRaw) || String(profilePhoneRaw).trim())
                          : interestRequestPhone;
                        if (!interestPhoneValid(phoneToSend)) {
                          setSharePhoneError(t('shareInterest.phoneRequired'));
                          return;
                        }
                        setSharePhoneError(null);
                        try {
                          if (!hasProfilePhoneForInterest) {
                            await setUserProfile(currentUser.uid, { personalPhone: phoneToSend.trim() });
                            if (refreshUserProfile) await refreshUserProfile();
                          }
                          await createInterest(shareToken, currentUser.uid, {
                            recipientEmail: currentUser.email || undefined,
                            recipientDisplayName: currentUser.displayName || undefined,
                            recipientPhone: phoneToSend.trim(),
                          });
                          const interest = await getInterestForRecipient(shareToken, currentUser.uid);
                          setShareInterest(interest || null);
                        } catch (e) {
                          console.error(e);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      {t('shareInterest.requestMoreInfoBroker')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!shareToken || !currentUser?.uid) return;
                      try {
                        await createInterest(shareToken, currentUser.uid, {
                          recipientEmail: currentUser.email || undefined,
                          recipientDisplayName: currentUser.displayName || undefined,
                        });
                        const interest = await getInterestForRecipient(shareToken, currentUser.uid);
                        setShareInterest(interest || null);
                      } catch (e) {
                        console.error(e);
                      }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    {t('shareInterest.requestMoreInfo')}
                  </button>
                )
              ) : shareInterest.status === 'requested' && !shareData?.ndaFileRef ? (
                <p className="text-sm text-indigo-800">
                  {pocketBrokerContactFlow
                    ? t('shareInterest.pocketRequestSentBroker')
                    : t(isBrokerSharedDeal ? 'shareInterest.requestSentNoNdaBroker' : 'shareInterest.requestSentNoNda')}
                </p>
              ) : shareInterest.status === 'requested' && shareData?.ndaFileRef && ndaUploadInterestFlow ? (
                <div className="space-y-2">
                  <p className="text-sm text-indigo-800">
                    {t(isBrokerSharedDeal ? 'shareInterest.ndaRequiredBroker' : 'shareInterest.ndaRequired')}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-indigo-300 rounded-lg bg-white cursor-pointer text-sm">
                      <FileUp className="w-4 h-4" />
                      {ndaFile ? ndaFile.name : t('shareInterest.chooseSignedNda')}
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setNdaFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    <button
                      type="button"
                      disabled={!ndaFile || submittingNda}
                      onClick={async () => {
                        if (!ndaFile || !shareToken || !shareInterest?.id) return;
                        setSubmittingNda(true);
                        try {
                          await submitNda(shareToken, shareInterest.id, ndaFile);
                          setNdaFile(null);
                          const interest = await getInterestForRecipient(shareToken, currentUser.uid);
                          setShareInterest(interest || null);
                        } catch (e) {
                          console.error(e);
                        } finally {
                          setSubmittingNda(false);
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {submittingNda ? '…' : t('shareInterest.submitNda')}
                    </button>
                  </div>
                </div>
              ) : shareInterest.status === 'requested' && shareData?.ndaFileRef && !ndaUploadInterestFlow ? (
                <p className="text-sm text-indigo-800">
                  {pocketBrokerContactFlow
                    ? t('shareInterest.pocketRequestSentBroker')
                    : t(isBrokerSharedDeal ? 'shareInterest.requestSentNoNdaBroker' : 'shareInterest.requestSentNoNda')}
                </p>
              ) : shareInterest.status === 'nda_submitted' ? (
                <p className="text-sm text-indigo-800">
                  {pocketBrokerContactFlow
                    ? t('shareInterest.pocketNdaSubmittedBroker')
                    : t(isBrokerSharedDeal ? 'shareInterest.ndaPendingApprovalBroker' : 'shareInterest.ndaPendingApproval')}
                </p>
              ) : shareInterest.status === 'nda_approved' ? (
                <p className="text-sm text-indigo-800">
                  {pocketBrokerContactFlow
                    ? t('shareInterest.pocketNdaPendingBroker')
                    : t(isBrokerSharedDeal ? 'shareInterest.waitingDocumentAccessBroker' : 'shareInterest.waitingDocumentAccess')}
                </p>
              ) : shareInterest.status === 'document_access_refused' ? (
                <p className="text-sm text-indigo-800">
                  {pocketBrokerContactFlow
                    ? t('shareInterest.pocketAccessRefusedBroker')
                    : t(isBrokerSharedDeal ? 'shareInterest.documentAccessRefusedBroker' : 'shareInterest.documentAccessRefused')}
                </p>
              ) : shareInterest.status === 'document_access_granted' || shareInterest.status === 'interest_confirmed' || shareInterest.status === 'cession_submitted' ? (
                <div className="space-y-4">
                  {/* Ligne d’actions seule : évite que le bouton Data room s’aligne en hauteur sur le bloc offre / cession */}
                  <div className="flex flex-wrap items-center gap-3">
                    {onOpenProjectDocuments && (
                      <button
                        type="button"
                        onClick={onOpenProjectDocuments}
                        className="inline-flex shrink-0 items-center gap-2 px-4 py-2 border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50"
                      >
                        <FileStack className="w-4 h-4 shrink-0" />
                        {t('building.projectDocuments')}
                      </button>
                    )}
                    {shareInterest.status === 'document_access_granted' && !ownedDiligenceFlow && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (!shareToken || !shareInterest?.id) return;
                          try {
                            await confirmInterest(shareToken, shareInterest.id);
                            const interest = await getInterestForRecipient(shareToken, currentUser.uid);
                            setShareInterest(interest || null);
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        className="inline-flex shrink-0 items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        {brokerMandatedSaleInterestFlow
                          ? t('shareInterest.confirmReadyForOffers')
                          : pocketBrokerContactFlow
                            ? t('shareInterest.pocketConfirmContactRequest')
                            : t('shareInterest.confirmInterest')}
                      </button>
                    )}
                  </div>
                  {(shareInterest.status === 'interest_confirmed' || shareInterest.status === 'cession_submitted') && (
                    <div className="w-full min-w-0 space-y-2">
                      {!shareData?.pocketDealMode &&
                      !brokerMandatedSaleInterestFlow &&
                      !ownedDiligenceFlow &&
                      shareInterest.status === 'interest_confirmed' && (
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="inline-flex items-center gap-2 px-3 py-2 border border-indigo-300 rounded-lg bg-white cursor-pointer text-sm">
                            <FileUp className="w-4 h-4" />
                            {cessionFile ? cessionFile.name : t('shareInterest.chooseCessionContract')}
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.doc,.docx"
                              onChange={(e) => setCessionFile(e.target.files?.[0] || null)}
                            />
                          </label>
                          <button
                            type="button"
                            disabled={!cessionFile || submittingCession}
                            onClick={async () => {
                              if (!cessionFile || !shareToken || !shareInterest?.id) return;
                              setSubmittingCession(true);
                              try {
                                await submitCessionContract(shareToken, shareInterest.id, cessionFile);
                                setCessionFile(null);
                                const interest = await getInterestForRecipient(shareToken, currentUser.uid);
                                setShareInterest(interest || null);
                              } catch (e) {
                                console.error(e);
                              } finally {
                                setSubmittingCession(false);
                              }
                            }}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {submittingCession ? '…' : t('shareInterest.submitCessionContract')}
                          </button>
                        </div>
                      )}
                      {shareData?.pocketDealMode &&
                        shareInterest.status === 'interest_confirmed' &&
                        pocketBrokerContactFlow && (
                        <p className="text-sm text-indigo-800">{t('shareInterest.pocketDealNoCessionUpload')}</p>
                      )}
                      {brokerMandatedSaleInterestFlow &&
                        shareInterest.status === 'interest_confirmed' &&
                        shareToken &&
                        shareInterest.id && (
                        <MandatedInterestOffersPanel
                          shareToken={shareToken}
                          interestId={shareInterest.id}
                          viewerRole="recipient"
                          ownerUid={shareData?.uid}
                          recipientUid={currentUser?.uid}
                          t={t}
                        />
                      )}
                      {!shareData?.pocketDealMode &&
                        !ownedDiligenceFlow &&
                        shareInterest.status === 'cession_submitted' && (
                        <span className="text-sm text-indigo-700">{t('shareInterest.cessionSubmittedWaiting')}</span>
                      )}
                    </div>
                  )}
                  {ownedDiligenceFlow &&
                    (shareInterest.status === 'document_access_granted' ||
                      shareInterest.status === 'interest_confirmed') && (
                    <p className="text-sm text-indigo-800">{t('shareInterest.ownedDiligenceReady')}</p>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {(!readOnly || showRentRegisterButton) && (
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {onOpenProjectDocuments && isPropertyOwner && !readOnly && (
                <button
                  type="button"
                  data-onboarding="dashboard-data-room"
                  onClick={onOpenProjectDocuments}
                  className="w-full sm:w-auto inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  <FileStack className="w-5 h-5" />
                  {t('building.projectDocuments')}
                </button>
              )}
              {showRentRegisterButton && (
                <button
                  type="button"
                  data-onboarding="dashboard-rent-register"
                  onClick={() => setRentRegisterModalOpen(true)}
                  className="w-full sm:w-auto inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                >
                  <ClipboardList className="w-5 h-5" />
                  {t('building.rentRegister')}
                </button>
              )}
              {!readOnly && !ownedDeal && (
              <button
                type="button"
                data-onboarding="dashboard-create-scenario"
                onClick={onCreateScenario}
                className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 inline-flex items-center justify-center gap-2"
              >
                {t('building.createScenario')}
              </button>
              )}
            </div>
          )}

          {staticScenarios && readOnly ? (
            <div className="space-y-2">
              <h3 className="font-medium text-gray-700 mb-2">{t('building.scenarios')}</h3>
              {staticScenarios
                .filter(
                  (s) =>
                    !['refinancing', 'renewal', 'optimization', 'other', 'returnProjection'].includes(
                      s.type,
                    ),
                )
                .map((sc) => (
                  <div
                    key={sc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <span className="text-sm">
                      {sc.title || sc.name || t('scenario.initialFinancing')}
                      {sc.type ? ` — ${scenarioTypeLabels[sc.type] || sc.type}` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => onViewScenario?.(sc)}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      {t('view')}
                    </button>
                  </div>
                ))}
            </div>
          ) : (
            <>
              {isPropertyOwner && (property?.sharedWithBrokerUids?.length > 0 || financementDossiers.length > 0 || privateLenderOffers.length > 0 || dealsForThisPropertyAsOwner.length > 0) && (
                <div className="mb-6 space-y-6">
                  {dealsForThisPropertyAsOwner.length > 0 && (() => {
                    const primaryDeal = dealsForThisPropertyAsOwner.find((d) => d.fundsDisbursedAt);
                    const bannerAnchor = primaryDeal ? (lenderInterestAnchorByUid[primaryDeal.lenderUid] ?? 1) : 1;
                    const cp = getCurrentPeriod({ interestPeriodAnchorDay: bannerAnchor });
                    const dateLocale = language === 'fr' ? 'fr-CA' : 'en-CA';
                    const [y, m] = cp.periodKey.split('-').map(Number);
                    const monthLabel =
                      bannerAnchor === 1
                        ? new Date(y, m - 1, 1).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' })
                        : `${formatDateShort(cp.periodStart)} – ${formatDateShort(cp.periodEnd)}`;
                    const paidDeals = dealsForThisPropertyAsOwner.filter((d) => ownerInterestPayments[d.id]?.status === 'paid');
                    if (paidDeals.length === 0) return null;
                    return (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          {t('building.interestPaymentReceived').replace('{month}', monthLabel)}
                        </p>
                      </div>
                    );
                  })()}
                  {dealsForThisPropertyAsOwner.filter((d) => d.fundsDisbursedAt).length > 0 && (() => {
                    const dealsWithNextPayment = dealsForThisPropertyAsOwner
                      .filter((d) => d.fundsDisbursedAt)
                      .map((deal) => {
                        const anchor = lenderInterestAnchorByUid[deal.lenderUid] ?? 1;
                        const { periodKey, periodStart, periodEnd } = getCurrentPeriod({
                          interestPeriodAnchorDay: anchor,
                        });
                        if (!isDealActiveInPeriod(deal, periodStart, periodEnd)) return null;
                        if (ownerInterestPayments[deal.id]?.status === 'paid') return null;
                        const tranches = ownerDealsTranches[deal.id] || [];
                        const expectedAmount = computeExpectedInterestForPeriod(deal, tranches, periodStart, periodEnd);
                        const expectedByDate = getExpectedPaymentDateForPeriod(deal, periodKey, anchor);
                        return { deal, expectedAmount, expectedByDate };
                      })
                      .filter(Boolean)
                      .filter((x) => x.expectedAmount > 0);
                    if (dealsWithNextPayment.length === 0) return null;
                    return (
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                          <DollarSign className="w-5 h-5" />
                          {t('building.nextInterestPayment')}
                        </h4>
                        <ul className="space-y-2 text-sm text-blue-800">
                          {dealsWithNextPayment.map(({ deal, expectedAmount, expectedByDate }) => (
                            <li key={deal.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                              <span className="font-medium">{formatMoneyDecimals(expectedAmount)}</span>
                              <span>{t('building.nextInterestPaymentDueBy')}</span>
                              <span>{formatDateShort(expectedByDate)}</span>
                              {deal.lenderDisplayName && (
                                <span className="text-blue-600">({deal.lenderDisplayName})</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                  <h3 className="font-medium text-gray-700 mb-2">{t('financementDossier.sectionTitle')} / {t('privateLenderOffer.sectionTitle')}</h3>
                  {(() => {
                    const actorIds = new Set([
                      ...financementDossiers.map((d) => d.brokerUid).filter(Boolean),
                      ...privateLenderOffers.map((o) => o.lenderUid).filter(Boolean),
                    ]);
                    if (actorIds.size === 0) {
                      return (
                        <p className="text-sm text-gray-500">{t('financementDossier.noDossiersYet')}</p>
                      );
                    }
                    return Array.from(actorIds).map((actorUid) => {
                      const dossiers = financementDossiers.filter((d) => d.brokerUid === actorUid);
                      const offers = privateLenderOffers.filter((o) => o.lenderUid === actorUid);
                      const displayName = dossiers[0]?.brokerDisplayName || offers[0]?.lenderDisplayName || actorUid;
                      return (
                        <div key={actorUid} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3">{displayName}</h4>
                          {dossiers.length > 0 && (
                            <div className="mb-3">
                              <h5 className="text-xs font-semibold text-gray-700 mb-2">{t('financementDossier.sectionTitle')}</h5>
                              <ul className="space-y-2">
                                {dossiers.map((d) => {
                                  const checklistLabel = d.checklistName?.trim() || (d.createdAt
                                    ? (d.createdAt?.toDate ? d.createdAt.toDate() : new Date(d.createdAt)).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                                    : null);
                                  const fullLabel = checklistLabel ? `${checklistLabel}` : t('financementDossier.requested');
                                  return (
                                    <li
                                      key={d.id}
                                      className="flex flex-wrap items-center justify-between gap-2 p-2 bg-white rounded border border-blue-100"
                                    >
                                      <span className="text-sm text-gray-700">
                                        {fullLabel}
                                        <span className="ml-2 text-gray-500">{t(`financementDossier.status_${d.status || 'not_started'}`)}</span>
                                      </span>
                                      {onOpenFinancementDossier && (
                                        <button
                                          type="button"
                                          onClick={() => onOpenFinancementDossier(d)}
                                          className="inline-flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded border border-blue-200 text-xs"
                                        >
                                          {t('financementDossier.open')}
                                        </button>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          {offers.length > 0 && (
                            <div className={dossiers.length > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                              <h5 className="text-xs font-semibold text-gray-700 mb-2">{t('privateLenderOffer.sectionTitle')}</h5>
                              <ul className="space-y-2">
                              {sortOffersByCreatedAtAsc(offers).map((offer) => (
                                <li key={offer.id} className={`p-2.5 rounded border ${offer.status === 'cancelled' || offer.status === 'deal_cancelled' ? 'bg-gray-50 border-gray-200 opacity-90' : 'bg-white border-emerald-100'}`}>
                                  <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                    <span className="text-sm text-gray-800">{getOfferDisplayLabel(offer)}</span>
                                    <span className="text-xs text-gray-500 shrink-0">{t(`privateLenderOffer.status_${offer.status || 'pending'}`)}</span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs">
                                    {offer.contractFileRef && offer.status !== 'rejected' && offer.status !== 'cancelled' && offer.status !== 'deal_cancelled' && (
                                      <>
                                        <a
                                          href="#"
                                          onClick={async (e) => {
                                            e.preventDefault();
                                            try {
                                              const url = await getOfferFileDownloadUrl(offer.contractFileRef.storagePath);
                                              window.open(url, '_blank');
                                            } catch (err) {
                                              console.error(err);
                                            }
                                          }}
                                          className="text-blue-600 hover:underline"
                                        >
                                          {t('privateLenderOffer.downloadContract')}
                                        </a>
                                        <button
                                          type="button"
                                          disabled={
                                            summarizingOfferId === offer.id ||
                                            !!(offer.contractSummaryFr || offer.contractSummaryEn || offer.contractSummary || (offerSummaryCache[offer.id] && (offerSummaryCache[offer.id].fr || offerSummaryCache[offer.id].en)))
                                          }
                                          onClick={async () => {
                                            const hasSummary = offer.contractSummaryFr || offer.contractSummaryEn || offer.contractSummary || (offerSummaryCache[offer.id] && (offerSummaryCache[offer.id].fr || offerSummaryCache[offer.id].en));
                                            if (hasSummary) {
                                              setExpandedSummaryOfferIds((prev) => {
                                                const next = new Set(prev);
                                                if (next.has(offer.id)) next.delete(offer.id);
                                                else next.add(offer.id);
                                                return next;
                                              });
                                              return;
                                            }
                                            setSummarizingOfferId(offer.id);
                                            try {
                                              const { summaryFr, summaryEn } = await summarizeOfferContract(offer.id);
                                              setOfferSummaryCache((c) => ({ ...c, [offer.id]: { fr: summaryFr, en: summaryEn } }));
                                              setExpandedSummaryOfferIds((prev) => new Set(prev).add(offer.id));
                                            } catch (err) {
                                              console.error(err);
                                              alert((t('share.error') || 'Error') + ': ' + (t('privateLenderOffer.summaryError') || err?.message));
                                            } finally {
                                              setSummarizingOfferId(null);
                                            }
                                          }}
                                          className="text-blue-600 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {summarizingOfferId === offer.id ? t('privateLenderOffer.summarizing') : t('privateLenderOffer.summarizeWithAI')}
                                        </button>
                                      </>
                                    )}
                                    {offer.status === 'accepted' && dealsForThisPropertyAsOwner.find((d) => d.offerId === offer.id) && (
                                      <button
                                        type="button"
                                        onClick={() => setSelectedDealForManagement(dealsForThisPropertyAsOwner.find((d) => d.offerId === offer.id))}
                                        className="inline-flex items-center gap-1 px-2 py-1 text-indigo-600 hover:bg-indigo-50 rounded border border-indigo-200 text-xs font-medium"
                                      >
                                        <LayoutList className="w-3.5 h-3.5" />
                                        {t('building.manageLoan')}
                                      </button>
                                    )}
                                    {offer.status === 'pending' && (
                                    <span className="inline-flex flex-wrap items-center gap-2">
                                      <label className="inline-flex items-center gap-1.5 px-2 py-1 border border-gray-300 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer text-xs">
                                        <FileUp className="w-3.5 h-3.5" />
                                        {signedContractFile && acceptingOfferId === offer.id ? signedContractFile.name : t('privateLenderOffer.chooseSignedContract')}
                                        <input
                                          type="file"
                                          accept=".pdf,.doc,.docx"
                                          className="hidden"
                                          onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                              setSignedContractFile(file);
                                              setAcceptingOfferId(offer.id);
                                            }
                                          }}
                                        />
                                      </label>
                                      <button
                                        type="button"
                                        disabled={!(signedContractFile && acceptingOfferId === offer.id) || submittingAcceptOfferId === offer.id}
                                        onClick={async () => {
                                          if (!(signedContractFile && acceptingOfferId === offer.id)) return;
                                          setSubmittingAcceptOfferId(offer.id);
                                          const errStep = { current: '' };
                                          try {
                                            errStep.current = '1'; // envoi contrat + mise à jour offre
                                            await acceptPrivateLenderOfferWithSignedContract(offer.id, signedContractFile);
                                            errStep.current = '2'; // lecture offre
                                            const updated = await getPrivateLenderOffer(offer.id);
                                            errStep.current = '3'; // vérification deal existant
                                            const existing = await getFinancedDealByOfferId(offer.id, property.uid);
                                            if (!existing && updated?.signedContractFileRef) {
                                              errStep.current = '4'; // création deal
                                              const lenderSettings = await getLenderSettings(offer.lenderUid);
                                              const dealId = await createFinancedDeal({
                                                offerId: offer.id,
                                                propertyId: property.id,
                                                ownerUid: property.uid,
                                                lenderUid: offer.lenderUid,
                                                lenderDisplayName: offer.lenderDisplayName || null,
                                                signedContractFileRef: updated.signedContractFileRef,
                                                principalAmount: typeof updated.principalAmount === 'number' ? updated.principalAmount : null,
                                                interestRate: typeof updated.interestRate === 'number' ? updated.interestRate : null,
                                                termMonths: typeof updated.termMonths === 'number' ? updated.termMonths : null,
                                                startDate: updated.loanStartDate || null,
                                                maturityDate: updated.loanMaturityDate || null,
                                                interestPaymentDue: updated.interestPaymentDue === 'beginning_of_month' ? 'beginning_of_month' : 'end_of_month',
                                                interestCalculationMode: updated.interestCalculationMode === 'monthly' ? 'monthly' : 'daily',
                                                lenderFundCode: lenderSettings?.fundCode || undefined,
                                              });
                                              if (dealId && typeof updated.principalAmount === 'number') {
                                                await createFinancedDealTranche({
                                                  dealId,
                                                  amount: updated.principalAmount,
                                                  startDate: updated.loanStartDate || new Date().toISOString().slice(0, 10),
                                                  interestRate: typeof updated.interestRate === 'number' ? updated.interestRate : null,
                                                  type: 'initial',
                                                });
                                              }
                                              errStep.current = '5'; // rafraîchissement liste
                                              if (typeof refreshFinancedDeals === 'function') {
                                                await refreshFinancedDeals();
                                              }
                                            }
                                          } catch (err) {
                                            console.error('Accept offer error', err);
                                            const stepLabels = { '1': 'envoi du contrat et mise à jour de l\'offre', '2': 'lecture de l\'offre', '3': 'vérification deal existant', '4': 'création du deal financé', '5': 'rafraîchissement' };
                                            const stepLabel = stepLabels[errStep.current] || errStep.current;
                                            const msg = (err?.message || String(err)) + ' (étape: ' + stepLabel + ')';
                                            alert((t('share.error') || 'Erreur') + ': ' + msg);
                                          } finally {
                                            setSubmittingAcceptOfferId(null);
                                            setAcceptingOfferId(null);
                                            setSignedContractFile(null);
                                          }
                                        }}
                                        className="px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 text-xs"
                                      >
                                        {submittingAcceptOfferId === offer.id ? '…' : t('privateLenderOffer.accept')}
                                      </button>
                                      <button
                                        type="button"
                                        disabled={rejectingOfferId === offer.id}
                                        onClick={async () => {
                                          if (!window.confirm(t('confirm'))) return;
                                          setRejectingOfferId(offer.id);
                                          try {
                                            await rejectPrivateLenderOffer(offer.id);
                                          } finally {
                                            setRejectingOfferId(null);
                                          }
                                        }}
                                        className="px-2 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50 disabled:opacity-50 text-xs"
                                      >
                                        {rejectingOfferId === offer.id ? '…' : t('privateLenderOffer.reject')}
                                      </button>
                                    </span>
                                  )}
                                </div>
                                {(() => {
                                  const summaryFr = offer.contractSummaryFr || (offerSummaryCache[offer.id]?.fr);
                                  const summaryEn = offer.contractSummaryEn || (offerSummaryCache[offer.id]?.en);
                                  const summaryLegacy = offer.contractSummary;
                                  const displaySummary = (language || 'en').startsWith('fr')
                                    ? (summaryFr || summaryLegacy)
                                    : (summaryEn || summaryLegacy);
                                  return displaySummary && (
                                    <div className="mt-2 border-t border-gray-100 pt-2">
                                      <button
                                        type="button"
                                        onClick={() => setExpandedSummaryOfferIds((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(offer.id)) next.delete(offer.id);
                                          else next.add(offer.id);
                                          return next;
                                        })}
                                        className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900"
                                      >
                                        {expandedSummaryOfferIds.has(offer.id) ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                        {t('privateLenderOffer.summaryTitle')}
                                      </button>
                                      {expandedSummaryOfferIds.has(offer.id) && (
                                        <div className="mt-2 pl-4 text-xs text-gray-600 whitespace-pre-wrap">
                                          {displaySummary}
                                          <p className="mt-2 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs not-italic">
                                            {t('privateLenderOffer.summaryDisclaimer')}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                                </li>
                              ))}
                            </ul>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {isPropertyOwner && cedantShares.some((s) => (shareInterestsByToken[s.id] || []).length > 0) && (
                <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <h3 className="font-medium text-slate-900 mb-3">{t('cession.cedentSectionTitle')}</h3>
                  {cedantShares.map((share) => {
                    const interests = shareInterestsByToken[share.id] || [];
                    if (interests.length === 0) return null;
                    return (
                      <div key={share.id} className="mb-4 last:mb-0">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">{t('cession.interestsForShare')}</h4>
                        <ul className="space-y-3">
                          {interests.map((int) => (
                            <li
                              key={int.id}
                              className="flex flex-wrap items-start justify-between gap-2 p-3 bg-white border border-slate-200 rounded-lg"
                            >
                              <div className="text-sm text-slate-800 min-w-0">
                                <div className="font-medium text-slate-900">
                                  {int.recipientDisplayName || int.recipientEmail || int.recipientUid}
                                </div>
                                {int.recipientEmail ? (
                                  <div className="text-slate-600 text-xs mt-0.5">{int.recipientEmail}</div>
                                ) : null}
                                {String(int.recipientPhone || '').trim() ? (
                                  <div className="text-slate-700 text-xs mt-1">
                                    {t('share.brokerPocketInterestPhone')}: {String(int.recipientPhone).trim()}
                                  </div>
                                ) : null}
                                <div className="text-slate-500 text-xs mt-1">
                                  — {t(`cession.status_${int.status || 'requested'}`)}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {int.status === 'nda_submitted' && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      await approveNda(share.id, int.id);
                                    }}
                                    className="text-sm px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                                  >
                                    {t('cession.approveNda')}
                                  </button>
                                )}
                                {int.status === 'requested' && share.ndaFileRef && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      if (!window.confirm(t('confirm'))) return;
                                      await refuseDocumentAccess(share.id, int.id);
                                    }}
                                    className="text-sm px-3 py-1.5 border border-slate-400 text-slate-700 rounded hover:bg-slate-50"
                                  >
                                    {t('cession.refuseDocumentAccess')}
                                  </button>
                                )}
                                {int.status === 'requested' && !share.ndaFileRef && (
                                  <div className="w-full flex flex-col gap-2">
                                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                      {t('cession.grantAccessNoNdaWarning')}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (!window.confirm(t('cession.grantAccessNoNdaConfirm'))) return;
                                          await grantDocumentAccess(share.id, int.id);
                                        }}
                                        className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 w-fit"
                                      >
                                        {t('cession.grantDocumentAccess')}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (!window.confirm(t('confirm'))) return;
                                          await refuseDocumentAccess(share.id, int.id);
                                        }}
                                        className="text-sm px-3 py-1.5 border border-slate-400 text-slate-700 rounded hover:bg-slate-50 w-fit"
                                      >
                                        {t('cession.refuseDocumentAccess')}
                                      </button>
                                    </div>
                                  </div>
                                )}
                                {(int.status === 'nda_approved' || int.status === 'document_access_refused' || int.status === 'document_access_granted' || int.status === 'interest_confirmed' || int.status === 'cession_submitted') && (
                                  <>
                                    {(int.status === 'nda_approved' || int.status === 'document_access_refused') && (
                                      <div className="flex flex-wrap gap-2">
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            await grantDocumentAccess(share.id, int.id);
                                          }}
                                          className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                                        >
                                          {t('cession.grantDocumentAccess')}
                                        </button>
                                        {int.status === 'nda_approved' && (
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              if (!window.confirm(t('confirm'))) return;
                                              await refuseDocumentAccess(share.id, int.id);
                                            }}
                                            className="text-sm px-3 py-1.5 border border-slate-400 text-slate-700 rounded hover:bg-slate-50"
                                          >
                                            {t('cession.refuseDocumentAccess')}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                    {(int.status === 'document_access_granted' || int.status === 'interest_confirmed' || int.status === 'cession_submitted') && (
                                      <>
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            await revokeDocumentAccess(share.id, int.id);
                                          }}
                                          className="text-sm px-3 py-1.5 border border-red-600 text-red-600 rounded hover:bg-red-50"
                                        >
                                          {t('cession.revokeDocumentAccess')}
                                        </button>
                                        {int.status === 'cession_submitted' && !share.ownedDiligence && (
                                          <button
                                            type="button"
                                            disabled={finalizingCession != null}
                                            onClick={async () => {
                                              if (!window.confirm(t('cession.finalizeConfirm'))) return;
                                              setFinalizingCession(share.id + int.id);
                                              try {
                                                await finalizeCession(share.id, int.id);
                                              } catch (e) {
                                                console.error(e);
                                              } finally {
                                                setFinalizingCession(null);
                                              }
                                            }}
                                            className="text-sm px-3 py-1.5 bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-50"
                                          >
                                            {finalizingCession === share.id + int.id ? '…' : t('cession.finalizeCession')}
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                              {share.brokerMandatedSale &&
                              int.status !== 'requested' &&
                              int.status !== 'listing_access_revoked' &&
                              currentUser?.uid ? (
                                <div className="w-full basis-full">
                                  <MandatedInterestOffersPanel
                                    shareToken={share.id}
                                    interestId={int.id}
                                    viewerRole="owner"
                                    ownerUid={currentUser.uid}
                                    recipientUid={int.recipientUid}
                                    t={t}
                                  />
                                </div>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}

              {!shareToken &&
                isBrokerViewingClientProperty &&
                brokerOwnedShares.some((s) => (brokerShareInterestsByToken[s.id] || []).length > 0) && (
                  <div className="mb-6 p-4 bg-violet-50 border border-violet-200 rounded-lg">
                    <h3 className="font-medium text-violet-900 mb-1">{t('share.brokerPocketInterestsTitle')}</h3>
                    <p className="text-xs text-violet-800 mb-3">{t('share.brokerPocketInterestShareHint')}</p>
                    {brokerOwnedShares.map((share) => {
                      const interests = brokerShareInterestsByToken[share.id] || [];
                      if (interests.length === 0) return null;
                      const pocketSimple =
                        share.pocketDealMode && !share.openToCession && !share.brokerMandatedSale;
                      return (
                        <div key={share.id} className="mb-4 last:mb-0">
                          <ul className="space-y-3">
                            {interests.map((int) => (
                              <li
                                key={int.id}
                                className="flex flex-col gap-3 p-3 bg-white border border-violet-100 rounded-lg sm:flex-row sm:items-start sm:justify-between"
                              >
                                <div className="text-sm text-slate-800 min-w-0 flex-1">
                                  <div className="font-medium text-slate-900">
                                    {int.recipientDisplayName || int.recipientEmail || int.recipientUid}
                                  </div>
                                  {int.recipientEmail ? (
                                    <div className="text-slate-600 text-xs mt-0.5 break-all">{int.recipientEmail}</div>
                                  ) : null}
                                  <div className="text-slate-700 text-xs mt-1">
                                    <span className="font-medium text-slate-800">{t('share.brokerPocketInterestPhone')}:</span>{' '}
                                    {String(int.recipientPhone || '').trim() || '—'}
                                  </div>
                                  <div className="text-slate-500 text-xs mt-1">
                                    —
                                    {t(
                                      pocketSimple
                                        ? `pocketInterest.status_${int.status || 'requested'}`
                                        : `cession.status_${int.status || 'requested'}`,
                                    )}
                                  </div>
                                </div>
                                {share.openToCession ? (
                                  <div className="flex flex-wrap gap-2 shrink-0">
                                    {int.status === 'nda_submitted' && (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          await approveNda(share.id, int.id);
                                        }}
                                        className="text-sm px-3 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                                      >
                                        {t('cession.approveNda')}
                                      </button>
                                    )}
                                    {int.status === 'requested' && share.ndaFileRef && (
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (!window.confirm(t('confirm'))) return;
                                          await refuseDocumentAccess(share.id, int.id);
                                        }}
                                        className="text-sm px-3 py-1.5 border border-slate-400 text-slate-700 rounded hover:bg-slate-50"
                                      >
                                        {t('cession.refuseDocumentAccess')}
                                      </button>
                                    )}
                                    {int.status === 'requested' && !share.ndaFileRef && (
                                      <div className="w-full flex flex-col gap-2">
                                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                          {t('cession.grantAccessNoNdaWarning')}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              if (!window.confirm(t('cession.grantAccessNoNdaConfirm'))) return;
                                              await grantDocumentAccess(share.id, int.id);
                                            }}
                                            className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 w-fit"
                                          >
                                            {t('cession.grantDocumentAccess')}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={async () => {
                                              if (!window.confirm(t('confirm'))) return;
                                              await refuseDocumentAccess(share.id, int.id);
                                            }}
                                            className="text-sm px-3 py-1.5 border border-slate-400 text-slate-700 rounded hover:bg-slate-50 w-fit"
                                          >
                                            {t('cession.refuseDocumentAccess')}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {(int.status === 'nda_approved' ||
                                      int.status === 'document_access_refused' ||
                                      int.status === 'document_access_granted' ||
                                      int.status === 'interest_confirmed' ||
                                      int.status === 'cession_submitted') && (
                                      <>
                                        {(int.status === 'nda_approved' || int.status === 'document_access_refused') && (
                                          <div className="flex flex-wrap gap-2">
                                            <button
                                              type="button"
                                              onClick={async () => {
                                                await grantDocumentAccess(share.id, int.id);
                                              }}
                                              className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                                            >
                                              {t('cession.grantDocumentAccess')}
                                            </button>
                                            {int.status === 'nda_approved' && (
                                              <button
                                                type="button"
                                                onClick={async () => {
                                                  if (!window.confirm(t('confirm'))) return;
                                                  await refuseDocumentAccess(share.id, int.id);
                                                }}
                                                className="text-sm px-3 py-1.5 border border-slate-400 text-slate-700 rounded hover:bg-slate-50"
                                              >
                                                {t('cession.refuseDocumentAccess')}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                        {(int.status === 'document_access_granted' ||
                                          int.status === 'interest_confirmed' ||
                                          int.status === 'cession_submitted') && (
                                          <>
                                            <button
                                              type="button"
                                              onClick={async () => {
                                                await revokeDocumentAccess(share.id, int.id);
                                              }}
                                              className="text-sm px-3 py-1.5 border border-red-600 text-red-600 rounded hover:bg-red-50"
                                            >
                                              {t('cession.revokeDocumentAccess')}
                                            </button>
                                            {int.status === 'cession_submitted' && !share.ownedDiligence && (
                                              <button
                                                type="button"
                                                disabled={finalizingCession != null}
                                                onClick={async () => {
                                                  if (!window.confirm(t('cession.finalizeConfirm'))) return;
                                                  setFinalizingCession(share.id + int.id);
                                                  try {
                                                    await finalizeCession(share.id, int.id);
                                                  } catch (e) {
                                                    console.error(e);
                                                  } finally {
                                                    setFinalizingCession(null);
                                                  }
                                                }}
                                                className="text-sm px-3 py-1.5 bg-emerald-700 text-white rounded hover:bg-emerald-800 disabled:opacity-50"
                                              >
                                                {finalizingCession === share.id + int.id ? '…' : t('cession.finalizeCession')}
                                              </button>
                                            )}
                                          </>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}

              <ScenarioList
                propertyId={property.id}
                shareToken={shareToken}
                baseScenarios={baseScenarios}
                shareCreatorInfo={shareCreatorInfo}
                shareFilterByCreatorUid={shareFilterByCreatorUid}
                additionalScenariosFromShares={additionalScenariosFromShares}
                readOnly={readOnly}
                onEdit={onEditScenario}
                onView={onViewScenario}
                excludeTypes={["returnProjection", "other"]}
                canEditScenario={!shareToken ? canEditScenarioFn : undefined}
                canDeleteScenario={!shareToken ? canDeleteScenarioFn : undefined}
                creatorUid={!shareToken ? currentUser?.uid : undefined}
              />
              {requestDossierModalOpen && !shareToken && isBrokerViewingClientProperty && (
                <RequestDossierModal
                  property={property}
                  existingDossiers={brokerDossiersForThisProperty}
                  onClose={() => setRequestDossierModalOpen(false)}
                  onRequested={() => setRequestDossierModalOpen(false)}
                  onOpenDossier={onOpenFinancementDossier}
                />
              )}
              {createOfferModalOpen && !shareToken && isBrokerViewingClientProperty && (
                <CreateOfferModal
                  property={property}
                  onClose={() => setCreateOfferModalOpen(false)}
                  onCreated={() => setCreateOfferModalOpen(false)}
                />
              )}
              {selectedDealForManagement && (() => {
                const deal = selectedDealForManagement;
                const tranches = selectedDealTranches.length ? selectedDealTranches : (ownerDealsTranches[deal.id] || []);
                const dealAnchor = lenderInterestAnchorByUid[deal.lenderUid] ?? 1;
                const schedulePeriods = getSchedulePeriodsForDeal(deal, {
                  interestPeriodAnchorDay: dealAnchor,
                });
                const dateLocale = language === 'fr' ? 'fr-CA' : 'en-CA';
                return (
                  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      <div className="flex items-center justify-between p-4 border-b">
                        <h3 className="text-lg font-semibold">{t('building.loanManagementTitle')}</h3>
                        <button type="button" onClick={() => setSelectedDealForManagement(null)} className="p-1 rounded hover:bg-gray-100">
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="p-4 space-y-4">
                        <div className="grid grid-cols-1 gap-2 text-sm">
                          <span className="text-gray-500">{t('privateLenderLoans.colAmount')}</span>
                          <span className="font-medium">{formatMoney(deal.principalAmount)}</span>
                          <span className="text-gray-500">{t('privateLenderLoans.colRate')}</span>
                          <span>{deal.interestRate != null ? `${deal.interestRate} %` : '—'}</span>
                          <span className="text-gray-500">{t('privateLenderLoans.colStart')}</span>
                          <span>{formatDateShort(deal.startDate)}</span>
                          <span className="text-gray-500">{t('privateLenderLoans.colMaturity')}</span>
                          <span>{formatDateShort(deal.maturityDate)}</span>
                          {deal.lenderDisplayName && (
                            <>
                              <span className="text-gray-500">{t('building.lender')}</span>
                              <span>{deal.lenderDisplayName}</span>
                            </>
                          )}
                        </div>
                        {tranches.length > 0 && (
                          <div>
                            <h4 className="font-medium text-gray-800 mb-2">{t('building.tranches')}</h4>
                            <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200 text-sm">
                              {tranches.map((tr, idx) => (
                                <li key={tr.id || idx} className="px-3 py-2 flex justify-between items-center gap-2">
                                  <span className="text-gray-700">{tr.type === 'initial' ? t('building.trancheInitial') : t('building.trancheAdditional')}{tr.startDate ? ` — ${formatDateShort(tr.startDate)}` : ''}</span>
                                  <span className="font-medium">{formatMoney(tr.amount)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {deal.dealReference && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <h4 className="font-medium text-amber-900 mb-1">{t('dealPayment.directDepositTitle')}</h4>
                            <p className="font-mono text-lg font-semibold text-amber-900 mb-1">{deal.dealReference}</p>
                            <p className="text-sm text-amber-800">{t('dealPayment.refInteracHelp')}</p>
                          </div>
                        )}
                        <div>
                          <h4 className="font-medium text-gray-800 mb-2">{t('building.interestPaymentsTracking')}</h4>
                          <div className="border border-gray-200 rounded-lg overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('building.period')}</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.interestExpected')}</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('building.dueDate')}</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.status')}</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.scheduleReceived')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {schedulePeriods.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} className="px-3 py-4 text-center text-gray-500">{t('privateLenderLoans.noSchedule')}</td>
                                  </tr>
                                ) : (
                                  schedulePeriods.map(({ periodKey: pk, periodStart: ps, periodEnd: pe }) => {
                                    const expected = computeExpectedInterestForPeriod(deal, tranches, ps, pe);
                                    const dueDate = getExpectedPaymentDateForPeriod(deal, pk, dealAnchor);
                                    const payment = ownerDealInterestPayments.find((p) => p.periodKey === pk || p.periodStart === ps);
                                    const paidAtStr = payment?.paidAt ? (payment.paidAt.toDate ? payment.paidAt.toDate().toISOString() : new Date(payment.paidAt).toISOString()).slice(0, 10) : '';
                                    const status = payment?.status === 'paid'
                                      ? t('building.paidOn').replace('{date}', formatDateShort(paidAtStr))
                                      : payment?.status === 'partial' || payment?.status === 'overpaid'
                                        ? t(`privateLenderLoans.interestStatus_${payment.status}`)
                                        : t('privateLenderLoans.interestStatus_pending');
                                    const [y, m] = pk.split('-').map(Number);
                                    const monthLabel =
                                      dealAnchor === 1
                                        ? new Date(y, m - 1, 1).toLocaleDateString(dateLocale, {
                                            month: 'long',
                                            year: 'numeric',
                                          })
                                        : `${formatDateShort(ps)} – ${formatDateShort(pe)}`;
                                    const statusClass = payment?.status === 'paid' ? 'text-green-600' : payment?.status === 'partial' || payment?.status === 'overpaid' ? 'text-amber-600' : 'text-gray-600';
                                    const creditApplied = payment?.creditApplied != null ? Number(payment.creditApplied) : 0;
                                    const creditAppliedDisplay = creditApplied > 0 ? formatMoneyDecimals(creditApplied) : null;
                                    return (
                                      <tr key={pk}>
                                        <td className="px-3 py-2 text-gray-800">{monthLabel}</td>
                                        <td className="px-3 py-2 text-right font-medium">{formatMoneyDecimals(expected)}</td>
                                        <td className="px-3 py-2 text-gray-600">{formatDateShort(dueDate)}</td>
                                        <td className="px-3 py-2">
                                          <span className={statusClass}>{status}</span>
                                          {creditAppliedDisplay && <span className="ml-2 text-xs text-gray-500">(crédit: {creditAppliedDisplay})</span>}
                                        </td>
                                        <td className="px-3 py-2 text-right">{payment?.paidAmount != null ? formatMoneyDecimals(payment.paidAmount) : '—'}</td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </div>
      {rentRegisterModalOpen && showRentRegisterButton && (
        <RentRegisterModal
          property={property}
          readOnly={!canEditRentRegister}
          onClose={() => setRentRegisterModalOpen(false)}
          onSave={canEditRentRegister ? onSaveRentRegister : undefined}
        />
      )}
      {directChatModal && dealChatUser && (
        <DirectChatModal
          onClose={() => setDirectChatModal(null)}
          conversationId={directChatModal.conversationId}
          ensureSpec={directChatModal.ensureSpec}
          title={directChatModal.title}
          subtitle={directChatModal.subtitle}
          currentUser={dealChatUser}
          currentStep="dashboard"
          currentProperty={property}
          onOpenDealFromChat={onOpenDealFromChat || undefined}
        />
      )}
    </div>
  );
};

export default BuildingDashboard;
